import {
  Component,
  ChangeDetectionStrategy,
  inject,
  signal,
  computed,
  effect,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  DestroyRef
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { PERMISSION_UI_DIRECTIVES } from '../../directives/permission.directive';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { TextareaModule } from 'primeng/textarea';
import { FloatLabelModule } from 'primeng/floatlabel';
import { SelectModule } from 'primeng/select';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { SplitButtonModule } from 'primeng/splitbutton';
import { CardModule } from 'primeng/card';
import { TabsModule } from 'primeng/tabs';
import { DatePickerModule } from 'primeng/datepicker';
import { TooltipModule } from 'primeng/tooltip';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { SelectButtonModule } from 'primeng/selectbutton';
import type { SelectButtonChangeEvent } from 'primeng/selectbutton';
import { ConfirmationService, type MenuItem } from 'primeng/api';
import { MenubarModule } from 'primeng/menubar';
import { Menu, MenuModule } from 'primeng/menu';
import { DrawerModule } from 'primeng/drawer';
import { ToastrService } from 'ngx-toastr';
import {
  customerInvoiceReadinessMessage,
  missingCustomerInvoiceFields
} from '../../core/customer-invoice-readiness';
import {
  missingProductInvoiceFields,
  productInvoiceReadinessMessage
} from '../../core/product-invoice-readiness';
import { finalize, catchError, of, merge, switchMap, throwError } from 'rxjs';
import { CustomerReadComponent } from '../customers/customer-read.component';
import { SubscriptionReadComponent } from '../subscriptions/subscription-read.component';
import { EntityAuditPanelComponent } from '../shared/entity-audit/entity-audit-panel.component';
import { ChargeFollowupDialogComponent } from './charge-followup-dialog.component';
import {
  SaleService,
  CreateSaleRequest,
  CreateSaleBillingTerms,
  CreateSaleLineRequest,
  CreateSaleLineTaxRequest,
  PreviewSaleLineRequest,
  SaleDTO,
  SaleLineDTO,
  ProductDTO as SaleProductDTO,
  CollectionMethod,
  TaxApplicationType,
  type MeasureUnitDTO as CoreMeasureUnitDTO,
  type TaxProductCodeDTO as CoreTaxProductCodeDTO
} from '../../services/sale.service';
import { CurrencyService, CurrencyDTO } from '../../services/currency.service';
import { CustomerService, CustomerDTO } from '../../services/customer.service';
import { SubscriptionService } from '../../services/subscription.service';
import type { SubscriptionDTO } from '../../core/models';
import { CompanyService } from '../../services/company.service';
import { ProductService, ProductDTO } from '../../services/product.service';
import { MeasureUnitService, MeasureUnitDTO } from '../../services/measure-unit.service';
import { TaxProductCodeService, TaxProductCodeDTO } from '../../services/tax-product-code.service';
import { TaxService, TaxDTO } from '../../services/tax.service';
import { SATObjetoImpService, SATObjetoImpDTO } from '../../services/sat-objeto-imp.service';
import { SATCFDIUseService } from '../../services/sat-cfdi-use.service';
import { PaymentFormService } from '../../services/payment-form.service';
import { PaymentMethodService } from '../../services/payment-method.service';
import { InvoiceService } from '../../services/invoice.service';
import { ChargeService } from '../../services/charge.service';
import { CfdiCancelMotivoService } from '../../services/cfdi-cancel-motivo.service';
import {
  AddressTypeEnum,
  ChargeOperationalStatusEnum,
  CfdiInvoiceStatusEnum,
  CollectionMethodEnum,
  EntityState,
  InvoiceStatusEnum,
  PaymentProviderEnum,
  PaymentRecordMethodEnum,
  PaymentRecordStatusEnum,
  SalePipelineStatusEnum,
  SaleProcessStatusEnum,
  toLineTaxApplicationTypeForCreate,
  type PaymentRecordMethodEnumValue,
  type PaymentRecordStatusEnumValue
} from '../../core/models';
import { formValueToApiDateTime, formValueToApiInstant, parseApiCalendarDate, parseApiDateTime, parseLocalDate } from '../../core/date-utils';
import {
  additionalFieldControl,
  rebuildAdditionalFieldsForm,
  serializeAdditionalFields,
  sortDynamicFields,
  dynamicFieldTypeLabel
} from '../../core/dynamic-fields.utils';
import { apiFailureMessage, httpErrorApiMessage } from '../../core/api-response-utils';
import { lineHashPayload } from '../../core/billing-hash.utils';
import {
  SaleEditField,
  SaleEditLevelEnum,
  saleAllowsField,
  saleEditLevelTooltip,
  saleFieldReadonlyTooltip,
  saleFullEditSaveConfirmMessage,
  type SaleEditFieldName,
  type SaleEditLevel
} from '../../core/sale-edit-fields';
import type {
  CompanyDTO,
  CustomerAddressDTO,
  DynamicField,
  DynamicFieldType,
  PaymentFormDTO,
  PaymentMethodDTO,
  SATCFDIUseDTO,
  ChargeDTO,
  PaymentAttemptDTO,
  InvoiceDTO,
  CfdiCancelMotivoDTO,
  ReadFileResponse
} from '../../core/models';

/** Resumen de datos fiscales mostrado antes de timbrar CFDI. */
export interface StampFiscalSummary {
  legalName: string;
  comercialName: string;
  taxId: string;
  taxRegime: string;
  address: string;
  email: string;
  paymentMethod: string;
  paymentForm: string;
  cfdiUse: string;
  totalLabel: string;
}
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
export interface SaleLineVm {
  tempId?: string;
  id?: number;
  productId?: number;
  product?: SaleProductDTO | ProductDTO;
  description: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
  measureUnitId?: number;
  measureUnit?: CoreMeasureUnitDTO;
  taxProductCodeId?: number;
  taxProductCode?: CoreTaxProductCodeDTO;
  satObjetoImpId?: string;
  satObjetoImp?: SATObjetoImpDTO;
  taxExempt?: boolean;
  taxInclusive?: boolean;
  taxes: CreateSaleLineTaxRequest[];
}

@Component({
  selector: 'app-sale-form',
  templateUrl: './sale-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  styles: [
    `
      :host ::ng-deep .sale-form-card.p-card .p-card-header {
        padding: 0;
      }
      :host ::ng-deep .sale-form-card.p-card .p-card-body {
        padding-top: 0.25rem;
      }
      :host ::ng-deep .sale-form-card .p-tablist,
      :host ::ng-deep .sale-form-card .p-tabview-tablist,
      :host ::ng-deep .sale-form-card .p-tabs-nav,
      :host ::ng-deep .sale-form-card [data-pc-section='tablist'] {
        padding-top: 0;
        margin-top: 0;
      }
      :host ::ng-deep .sale-form-card .p-tabpanels,
      :host ::ng-deep .sale-form-card .p-tabpanel {
        padding-top: 0.5rem;
      }
      /* FloatLabel + InputGroup: el label no debe quedar bajo el borde */
      :host ::ng-deep .dyn-field-floatlabel.p-floatlabel {
        overflow: visible;
      }
      :host ::ng-deep .dyn-field-floatlabel .p-inputgroup {
        overflow: visible;
      }
      :host ::ng-deep .dyn-field-floatlabel > label {
        z-index: 3;
        background-color: var(--p-content-background, #ffffff);
        padding-inline: 0.35rem;
        margin-inline-start: 0.5rem;
        line-height: 1;
        pointer-events: none;
      }
      :host ::ng-deep .customer-details-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .customer-details-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .subscription-details-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .subscription-details-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
    `
  ],
  imports: [
    CommonModule,
    PERMISSION_UI_DIRECTIVES,
    ReactiveFormsModule,
    FormsModule,
    InputTextModule,
    InputNumberModule,
    TextareaModule,
    FloatLabelModule,
    SelectModule,
    AutoCompleteModule,
    ButtonModule,
    SplitButtonModule,
    CardModule,
    TabsModule,
    DatePickerModule,
    TooltipModule,
    CheckboxModule,
    ConfirmDialogModule,
    DialogModule,
    SelectButtonModule,
    IconFieldModule,
    InputIconModule,
    InputGroupModule,
    InputGroupAddonModule,
    MenubarModule,
    MenuModule,
    DrawerModule,
    CustomerReadComponent,
    SubscriptionReadComponent,
    EntityAuditPanelComponent,
    ChargeFollowupDialogComponent
  ],
})
export class SaleFormComponent {
  private fb = inject(FormBuilder);
  private sanitizer = inject(DomSanitizer);
  private saleService = inject(SaleService);
  private invoiceService = inject(InvoiceService);
  private chargeService = inject(ChargeService);
  private currencyService = inject(CurrencyService);
  private customerService = inject(CustomerService);
  private subscriptionService = inject(SubscriptionService);
  private companyService = inject(CompanyService);
  private productService = inject(ProductService);
  private measureUnitService = inject(MeasureUnitService);
  private taxProductCodeService = inject(TaxProductCodeService);
  private taxService = inject(TaxService);
  private satObjetoImpService = inject(SATObjetoImpService);
  private satCfdiUseService = inject(SATCFDIUseService);
  private paymentFormService = inject(PaymentFormService);
  private paymentMethodService = inject(PaymentMethodService);
  private cfdiCancelMotivoService = inject(CfdiCancelMotivoService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private toastr = inject(ToastrService);
  private confirmationService = inject(ConfirmationService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  /** Expuesto al template para comparar métodos de cobro. */
  readonly CollectionMethodEnum = CollectionMethodEnum;
  readonly SaleEditField = SaleEditField;
  readonly SaleEditLevelEnum = SaleEditLevelEnum;

  @ViewChild('productLineRef') productLineRef?: ElementRef<HTMLElement>;
  @ViewChild('chargeActionsMenu') chargeActionsMenu?: Menu;
  @ViewChild('invoiceActionsMenu') invoiceActionsMenu?: Menu;

  /**
   * Formulario raíz con grupos anidados alineados a las secciones del tab General (validación por sección):
   * - `general`: cliente, moneda, tipo de venta, método de cobro, estados, notas y totales.
   * - `paymentTerms`: cuotas, frecuencia, cobro y plazos.
   * - `invoicing`: datos fiscales opcionales y estrategia de emisión.
   */
  saleForm: FormGroup;
  /** Sección General del tab General. */
  generalForm: FormGroup;
  /** Sección Facturación (dentro de General). */
  invoicingForm: FormGroup;
  /** Sección Términos de pago (dentro de General). */
  paymentTermsForm: FormGroup;
  /** Campos adicionales dinámicos (configuración de empresa). */
  additionalFieldsForm: FormGroup;
  /** Definición de campos adicionales cargada de /companies/me. */
  dynamicFields = signal<DynamicField[]>([]);
  /** Forzar reactividad de canSaveDraft al cambiar campos adicionales. */
  private additionalFieldsRefresh = signal(0);
  loading = signal(false);
  /** Overlay de carga inicial o refresh de la venta (no guardado ni otras acciones). */
  formDataLoading = signal(false);
  /** True mientras se genera el hash para copiar el enlace público del recibo. */
  receiptLinkCopyLoading = signal(false);
  downloadPdfLoading = signal(false);
  downloadXmlLoading = signal(false);
  /** Carga del PDF para abrir el visor (clic principal del split CFDI). */
  cfdiPdfViewerOpening = signal(false);
  cfdiPdfViewerVisible = signal(false);
  cfdiPdfViewerSafeUrl = signal<SafeResourceUrl | null>(null);
  /** Timbrado POST /sales/{id}/stamp. */
  stampSaleLoading = signal(false);
  /** PUT /sales/{id}/invoicing */
  invoicingProfileUpdateLoading = signal(false);
  error = signal<string | null>(null);
  isEditMode = signal(false);
  editingSaleId = signal<number | null>(null);
  currentSale = signal<SaleDTO | null>(null);

  /**
   * Nivel de edición autorizado por el backend (`Sale.editLevel`).
   * En alta (sin venta) se trata como FULL.
   */
  editLevel = computed((): SaleEditLevel => {
    const level = this.currentSale()?.editLevel;
    if (level === SaleEditLevelEnum.ADDITIONAL_ONLY
      || level === SaleEditLevelEnum.FISCAL_DATA
      || level === SaleEditLevelEnum.FULL) {
      return level;
    }
    if (!this.isEditMode() || this.currentSale() == null) {
      return SaleEditLevelEnum.FULL;
    }
    // Fallback si el API aún no envía editLevel
    const sale = this.currentSale()!;
    if (sale.isStamped === true || sale.invoice != null) {
      return SaleEditLevelEnum.ADDITIONAL_ONLY;
    }
    if (sale.collectionMethod === CollectionMethodEnum.RECURRING_CHARGE) {
      return SaleEditLevelEnum.FISCAL_DATA;
    }
    const hasPaid = this.charges().some((c) => c.status === ChargeOperationalStatusEnum.PAID);
    if (hasPaid) {
      return SaleEditLevelEnum.FISCAL_DATA;
    }
    return SaleEditLevelEnum.FULL;
  });

  /** True cuando el campo no es editable según editLevel o es inmutable tras el alta. */
  isFieldReadonly(field: SaleEditFieldName): boolean {
    if (!this.isEditMode() && this.currentSale() == null) {
      return false;
    }
    return !saleAllowsField(this.editLevel(), field);
  }

  fieldReadonlyTooltip(field: SaleEditFieldName): string | undefined {
    return this.isFieldReadonly(field)
      ? saleFieldReadonlyTooltip(this.editLevel(), field)
      : undefined;
  }

  /** Compat: formulario "bloqueado" solo si no hay ningún campo editable de cabecera/líneas. */
  isReadonly = computed(() => this.editLevel() !== SaleEditLevelEnum.FULL && this.isEditMode());

  canEditLines = computed(() => saleAllowsField(this.editLevel(), SaleEditField.LINES));
  canEditNotes = computed(() => saleAllowsField(this.editLevel(), SaleEditField.NOTES));
  canEditAdditionalFields = computed(() =>
    saleAllowsField(this.editLevel(), SaleEditField.ADDITIONAL_FIELDS)
  );
  canEditFiscalFields = computed(() =>
    saleAllowsField(this.editLevel(), SaleEditField.PAYMENT_METHOD_ID)
  );

  /**
   * Permite usar «Facturar» (timbrar CFDI): `Sale.status` ISSUED, PARTIALLY_PAID o PAID
   * (alineado con `SaleStatus` en API).
   */
  canStampSaleCfdi = computed(() => {
    const sale = this.currentSale();
    if (sale?.invoice != null) return false;
    const s = sale?.status;
    return (
      s === InvoiceStatusEnum.ISSUED ||
      s === InvoiceStatusEnum.PARTIALLY_PAID ||
      s === InvoiceStatusEnum.PAID
    );
  });

  /** True cuando la venta está cancelada (`CANCELLED` / legacy `VOID`). */
  isSaleCancelled = computed(() => {
    const raw = String(this.currentSale()?.status ?? '');
    return raw === InvoiceStatusEnum.CANCELLED || raw === InvoiceStatusEnum.VOID;
  });

  /** Las ventas de cobros bajo demanda no se cancelan desde la app (la API lo rechaza). */
  isMerchantInitiatedSale = computed(() =>
    this.currentSale()?.collectionMethod === CollectionMethodEnum.MERCHANT_INITIATED
  );

  /** True cuando la venta ya fue eliminada (soft-delete: `state = INACTIVE`). */
  isSaleInactive = computed(() => this.currentSale()?.state === EntityState.INACTIVE);

  /** Eliminar solo si está cancelada y todavía activa. */
  canDeleteSale = computed(() => this.isSaleCancelled() && !this.isSaleInactive());

  /** Hay pagos aplicados: la cancelación de venta debe advertir y anularlos. */
  hasAppliedPayments = computed(() => {
    const sale = this.currentSale();
    if (sale?.status === InvoiceStatusEnum.PAID || sale?.status === InvoiceStatusEnum.PARTIALLY_PAID) {
      return true;
    }
    return this.charges().some((c) => c.status === ChargeOperationalStatusEnum.PAID);
  });

  appliedPaymentsCount = computed(() =>
    this.charges().filter((c) => c.status === ChargeOperationalStatusEnum.PAID).length
  );

  lines = signal<SaleLineVm[]>([]);
  /** Señal para forzar re-evaluación de canSaveDraft tras agregar/eliminar líneas. */
  private draftButtonRefresh = signal(0);
  /** Reactiva canSaveDraft ante cambios en el tab General (cliente, moneda, notas, totales ocultos). */
  private generalTabDraftRefresh = signal(0);

  /**
   * Borrador habilitado con datos del tab General: cliente, moneda, términos de pago,
   * facturación (si aplica) y campos adicionales.
   */
  canSaveDraft = computed(() => {
    this.lines();
    this.draftButtonRefresh();
    this.generalTabDraftRefresh();
    this.additionalFieldsRefresh();
    this.paymentTermsDraftRefresh();
    if (this.loading()) return false;
    if (this.isEditMode() && !this.canEditLines() && !this.canEditNotes() && !this.canEditAdditionalFields() && !this.canEditFiscalFields()) {
      return false;
    }
    // Alta / FULL: exigir datos core válidos
    if (!this.isEditMode() || this.editLevel() === SaleEditLevelEnum.FULL) {
      const v = this.generalForm.getRawValue();
      const hasCore =
        v.customerId != null &&
        v.currencyId != null &&
        v.processStatus != null &&
        String(v.processStatus).trim() !== '' &&
        v.status != null &&
        String(v.status).trim() !== '';
      return (
        hasCore &&
        this.generalForm.valid &&
        this.paymentTermsForm.valid &&
        this.invoicingForm.valid &&
        this.additionalFieldsForm.valid
      );
    }
    // Niveles parciales: controles habilitados (notas, status, CFDI si aplica, adicionales)
    return this.generalForm.valid && this.additionalFieldsForm.valid && this.invoicingForm.valid;
  });

  /** Las ventas no timbran CFDI por este flujo (solo facturas). */
  canEmitCfdi = computed(() => false);

  /**
   * Id de factura vinculada: respuesta de venta o primer cargo con `invoiceId` (p. ej. tras cargar recibos).
   */
  linkedInvoiceId = computed((): number | null => {
    const sale = this.currentSale();
    const fromSale = sale?.invoiceId ?? sale?.invoice?.id;
    if (fromSale != null && typeof fromSale === 'number' && !Number.isNaN(fromSale)) {
      return fromSale;
    }
    const fromCharge = this.charges().find((c) => c.invoiceId != null)?.invoiceId;
    if (fromCharge != null && typeof fromCharge === 'number' && !Number.isNaN(fromCharge)) {
      return fromCharge;
    }
    return null;
  });

  /** Id de suscripción vinculada a la venta (si existe). */
  linkedSubscriptionId = computed((): number | null => {
    const id = this.currentSale()?.subscription?.id;
    return id != null && typeof id === 'number' && !Number.isNaN(id) ? id : null;
  });

  linkedSubscriptionStatus = computed((): string | null => {
    return this.currentSale()?.subscription?.status ?? null;
  });

  isLinkedSubscriptionInactive = computed(() => {
    return this.currentSale()?.subscription?.state === EntityState.INACTIVE;
  });

  lineModalOpen = signal(false);
  lineModalShown = signal(false);
  lineModalMode = signal<'create' | 'edit'>('create');
  /** Datos fiscales e impuestos del diálogo de línea: ocultos por defecto. */
  lineFiscalDetailsExpanded = signal(false);
  editingLineIndex = signal<number | null>(null);
  lineForm: FormGroup;
  lineSaving = signal(false);
  /** Subtotal calculado en el modal de línea (cantidad × precio). */
  lineSubtotal = signal(0);
  lineRemoving = signal(false);

  /** Diálogo de confirmación al cerrar venta (ISSUED). */
  closeSaleConfirmVisible = signal(false);

  /**
   * Confirmación al guardar en nivel FULL (regeneración de cargos).
   * Guarda el status pendiente hasta que el usuario confirme o cancele.
   */
  fullEditSaveConfirmVisible = signal(false);
  private pendingFullEditSaveStatus = signal<'DRAFT' | 'ISSUED' | undefined>(undefined);
  readonly fullEditSaveConfirmMessage = saleFullEditSaveConfirmMessage();

  /** Diálogo de confirmación para cancelar la venta. */
  cancelSaleConfirmVisible = signal(false);
  cancelPaymentsConfirmVisible = signal(false);
  cancelSaleLoading = signal(false);
  cancelSaleError = signal<string | null>(null);
  cancelSaleReason = signal('');
  readonly cancelReasonMaxLength = 500;

  /** Diálogo para cancelar un pago aplicado. */
  cancelPaymentDialogVisible = signal(false);
  cancelPaymentLoading = signal(false);
  cancelPaymentError = signal<string | null>(null);
  cancelPaymentReason = signal('');
  cancelPaymentCharge = signal<ChargeDTO | null>(null);

  /** Diálogo de confirmación para eliminar una venta ya cancelada. */
  deleteSaleConfirmVisible = signal(false);
  deleteSaleLoading = signal(false);
  deleteSaleError = signal<string | null>(null);

  /** Diálogo de confirmación antes de timbrar CFDI (Facturar). */
  stampSaleConfirmVisible = signal(false);
  /** Advertencia extra si la forma de pago es «99 - Por definir». */
  stampPaymentForm99WarningVisible = signal(false);
  /** Datos fiscales del receptor/CFDI al abrir el diálogo de timbrado. */
  stampFiscalSummary = signal<StampFiscalSummary | null>(null);

  productOptions = signal<ProductDTO[]>([]);
  productLoading = signal(false);
  customerOptions = signal<CustomerDTO[]>([]);
  customerLoading = signal(false);
  selectedCustomer = signal<CustomerDTO | null>(null);
  /** Cliente completo (régimen, país y direcciones) para validar autoInvoice. */
  invoiceCustomer = signal<CustomerDTO | null>(null);
  invoiceCustomerStatus = signal<'empty' | 'loading' | 'ready'>('empty');
  isCustomerDetailsOpen = signal(false);
  customerDetailsLoading = signal(false);
  selectedSubscription = signal<SubscriptionDTO | null>(null);
  isSubscriptionDetailsOpen = signal(false);
  subscriptionDetailsLoading = signal(false);
  currencyOptions = signal<CurrencyDTO[]>([]);
  measureUnitOptions = signal<MeasureUnitDTO[]>([]);
  taxProductCodeOptions = signal<TaxProductCodeDTO[]>([]);
  satObjetoImpOptions = signal<SATObjetoImpDTO[]>([]);
  satCfdiUseOptions = signal<SATCFDIUseDTO[]>([]);
  taxOptions = signal<TaxDTO[]>([]);
  paymentFormOptions = signal<PaymentFormDTO[]>([]);
  paymentMethodOptions = signal<PaymentMethodDTO[]>([]);
  currencyLoading = signal(false);

  lineTaxModalOpen = signal(false);
  lineTaxModalShown = signal(false);
  lineTaxForm: FormGroup;
  lineTaxSaving = signal(false);
  editingLineTaxIndex = signal<number | null>(null);

  /** Índices de líneas con desglose de impuestos expandido */
  expandedLineIndices = signal<Set<number>>(new Set());

  activeTab = signal<string>('general');
  seguimientoDialogVisible = signal(false);
  seguimientoDialogChargeId = signal<number | null>(null);
  seguimientoDialogSubtitle = signal<string | null>(null);

  /** Reactiva botones de guardado ante cambios en términos de pago / facturación. */
  private paymentTermsDraftRefresh = signal(0);

  /**
   * Pie «Crear/Guardar borrador» y «Cerrar venta»: exige datos generales, términos y facturación válidos.
   */
  footerDraftSubmitEnabled = computed(() => {
    this.paymentTermsDraftRefresh();
    if (this.loading()) return false;
    return this.canSaveDraft();
  });

  /** Pie: guardar cambios en venta ya cerrada (cualquier editLevel con campos permitidos). */
  showSaleUpdateInFooter = computed(
    () =>
      this.isEditMode() &&
      this.currentSale() != null &&
      this.currentSale()!.status !== InvoiceStatusEnum.DRAFT &&
      (this.canEditNotes() ||
        this.canEditAdditionalFields() ||
        this.canEditFiscalFields() ||
        this.canEditLines())
  );

  charges = signal<ChargeDTO[]>([]);
  chargesLoading = signal(false);
  /** CFDIs (Invoice) asociados a la venta. */
  invoices = signal<InvoiceDTO[]>([]);
  invoicesLoading = signal(false);
  attachments = signal<ReadFileResponse[]>([]);
  attachmentsLoading = signal(false);
  uploadingAttachments = signal(false);
  /** Ítems del menú overlay de acciones por fila en CFDI's. */
  invoiceRowMenuItems = signal<MenuItem[]>([]);
  /** Ids de cargos con fila de intentos de pago expandida. */
  expandedChargeIds = signal<Set<number>>(new Set());
  /** Intentos de pago cacheados por chargeId (solo se cargan al expandir). */
  chargeAttemptsById = signal<Record<number, PaymentAttemptDTO[]>>({});
  /** chargeIds con carga de intentos en curso. */
  chargeAttemptsLoadingIds = signal<Set<number>>(new Set());
  /** Id del cargo con reintento de cobro en curso; null si no hay operación en curso. */
  chargeCaptureLoadingId = signal<number | null>(null);
  /** Ítems del menú overlay de acciones por fila en Recibos de pago. */
  chargeRowMenuItems = signal<MenuItem[]>([]);
  /** Diálogo para reenviar correo de liga de pago. */
  recallEmailDialogVisible = signal(false);
  recallEmail = signal('');
  recallChargeId = signal<number | null>(null);
  recallLoading = signal(false);
  /** Recibo MANUAL: al enviar se crea la liga y el método pasa a LINK. */
  recallConvertsManualCharge = signal(false);
  /** Diálogo para reenviar CFDI (PDF y XML) por correo. */
  resendCfdiDialogVisible = signal(false);
  resendCfdiEmail = signal('');
  resendCfdiLoading = signal(false);
  resendCfdiInvoiceId = signal<number | null>(null);
  /** Diálogo para cancelar un CFDI vigente. */
  cancelCfdiDialogVisible = signal(false);
  cancelCfdiLoading = signal(false);
  cancelCfdiInvoiceId = signal<number | null>(null);
  cancelCfdiMotivo = signal('');
  cancelCfdiFolioSustitucion = signal('');
  cfdiCancelMotivoOptions = signal<CfdiCancelMotivoDTO[]>([]);
  readonly cfdiCancelMotivoSelectOptions = computed(() =>
    this.cfdiCancelMotivoOptions().map((motivo) => ({
      value: motivo.name ?? '',
      label: `${motivo.name} - ${motivo.description}`,
      requiresRelatedCfdi: !!motivo.requiresRelatedCfdi
    }))
  );
  readonly selectedCancelCfdiMotivo = computed(() =>
    this.cfdiCancelMotivoSelectOptions().find((option) => option.value === this.cancelCfdiMotivo())
  );
  /** Diálogo para registrar o editar pago manual. */
  payChargeDialogVisible = signal(false);
  payChargeId = signal<number | null>(null);
  payChargeLoading = signal(false);
  payChargeMode = signal<'create' | 'edit'>('create');
  /** Fecha de inicio de cobro cuando se paga por adelantado un recibo Programado. */
  payChargeScheduledStart = signal<string | null>(null);
  payChargeForm!: FormGroup;

  /** Sin tipo FISCAL en este formulario: asteriscos de obligatoriedad CFDI desactivados. */
  isFiscalBillingType = computed(() => false);

  /** CFDI ya timbrado: sección Facturación en solo lectura. */
  isInvoicingProfileStampedLocked = computed(() => this.currentSale()?.invoice != null || this.currentSale()?.isStamped === true);

  /**
   * Venta en estado cerrado operativo (misma condición que timbrar CFDI vía barra).
   * Permite editar método/forma de pago y uso CFDI si aún no está timbrada.
   */
  saleIsClosedForInvoicingProfile = computed(() => {
    const s = this.currentSale()?.status;
    return (
      s === InvoiceStatusEnum.ISSUED ||
      s === InvoiceStatusEnum.PARTIALLY_PAID ||
      s === InvoiceStatusEnum.PAID
    );
  });

  /** Método de pago, forma de pago y uso de CFDI editables según editLevel del backend. */
  canEditInvoicingPaymentAndCfdiFields = computed(() => this.canEditFiscalFields());

  /** Pie: botón PUT /sales/{id}/invoicing — se unifica en Guardar cambios vía PUT principal. */
  showInvoicingProfileUpdateInFooter = computed(() => false);

  /** Venta cerrada (no borrador): se ocultan Cerrar venta y Guardar borrador en el pie. */
  saleIsClosedOperationalFooter = computed(
    () =>
      this.isEditMode() &&
      this.currentSale() != null &&
      this.currentSale()!.status !== InvoiceStatusEnum.DRAFT
  );

  /** Términos de pago por defecto de la empresa (GET /companies/me). */
  private companyPaymentTerms: { daysUntilExpiry: number | null } | null = null;

  /** Embudo comercial — API `processStatus` (`SaleProcessStatusEnum`). */
  salePipelineStatusOptions: { value: (typeof SalePipelineStatusEnum)[keyof typeof SalePipelineStatusEnum]; label: string }[] = [
    { value: SalePipelineStatusEnum.ACCEPTED, label: 'Aceptado' },
    { value: SalePipelineStatusEnum.CONTRACT_ATTACHMENT, label: 'Contrato/Anexo' },
    { value: SalePipelineStatusEnum.OC, label: 'OC' },
    { value: SalePipelineStatusEnum.INVOICED, label: 'Facturado' },
    { value: SalePipelineStatusEnum.INVOICE_IMPORTED, label: 'Factura Ingresada' },
    { value: SalePipelineStatusEnum.PAID, label: 'Pagado' },
    { value: SalePipelineStatusEnum.LOST, label: 'Perdido' }
  ];

  /** Estado operativo — API `status` (`SaleProcessStatus`). */
  saleProcessStatusOptions: { value: (typeof SaleProcessStatusEnum)[keyof typeof SaleProcessStatusEnum]; label: string }[] = [
    { value: SaleProcessStatusEnum.DRAFT, label: 'Borrador' },
    { value: SaleProcessStatusEnum.QUOTATION, label: 'Cotización' },
    { value: SaleProcessStatusEnum.OC, label: 'Orden de compra' },
    { value: SaleProcessStatusEnum.IN_BILLING, label: 'En facturación' },
    { value: SaleProcessStatusEnum.BILLED, label: 'Facturado' }
  ];

  /** Ayuda del estado operativo (cabecera, solo lectura; API `status`). */
  readonly statusTooltip =
    'Fase operativa de la venta (borrador, cotización, facturación, etc.). Se actualiza automáticamente según el flujo; no es editable aquí.';

  /**
   * Catálogo completo de métodos de cobro (orden: liga, cargo recurrente, transferencia, manual).
   * El select filtra según tipo de venta; las etiquetas de cargos usan la lista completa.
   */
  private readonly allCollectionMethodOptions: { value: CollectionMethod; label: string }[] = [
    { value: CollectionMethodEnum.LINK, label: 'Liga de pago' },
    { value: CollectionMethodEnum.RECURRING_CHARGE, label: 'Cobro recurrente' },
    { value: CollectionMethodEnum.MANUAL, label: 'Manual' },
    { value: CollectionMethodEnum.MERCHANT_INITIATED, label: 'Cobro bajo demanda' }
  ];

  /**
   * Venta única: liga, transferencia, manual.
   * Suscripción: liga, cargo recurrente, transferencia, manual.
   */
  collectionMethodOptions(): { value: CollectionMethod; label: string }[] {
    const current = this.generalForm?.get('collectionMethod')?.value;
    const options = this.allCollectionMethodOptions.filter(
      (o) => o.value !== CollectionMethodEnum.MERCHANT_INITIATED || current === CollectionMethodEnum.MERCHANT_INITIATED
    );
    if (this.isSubscriptionSaleType()) {
      return options;
    }
    return options.filter(
      (o) => o.value !== CollectionMethodEnum.RECURRING_CHARGE
    );
  }

  /** Etiqueta legible del método de cobro en la tabla de cargos (mismas opciones que el formulario). */
  getChargeCollectionMethodLabel(method: string | undefined): string {
    if (method == null || String(method).trim() === '') return '-';
    return this.allCollectionMethodOptions.find((o) => o.value === method)?.label ?? method;
  }

  chargeCollectionValidity(charge: ChargeDTO) {
    return this.collectionValidity(
      parseApiCalendarDate(charge.collectionStartDate)?.toJSDate() ?? null,
      parseApiCalendarDate(charge.dueDate)?.toJSDate() ?? null
    );
  }

  private isDueDateExpired(due: Date | null): boolean {
    return this.daysOverdue(due) > 0;
  }

  private daysOverdue(due: Date | null): number {
    if (!due) return 0;
    const today = new Date();
    const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());
    const todayDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const days = Math.round((todayDay.getTime() - dueDay.getTime()) / 86_400_000);
    return days > 0 ? days : 0;
  }

  overdueTooltip(due: Date | null): string {
    const days = this.daysOverdue(due);
    if (days <= 0) return '';
    return days === 1 ? 'Vencida hace 1 día' : `Vencida hace ${days} días`;
  }

  /** Avance de la vigencia del cobro: verde 0–70 %, amarillo 71–99 %, rojo 100 %. */
  private collectionValidity(start: Date | null, due: Date | null): {
    start: Date;
    due: Date;
    percent: number;
    barClass: string;
    expired: boolean;
    remainingLabel: string;
  } | null {
    if (!start || !due) return null;
    const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
    const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate()).getTime();
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const span = dueDay - startDay;
    let percent: number;
    if (today >= dueDay) {
      percent = 100;
    } else if (today <= startDay || span <= 0) {
      percent = 0;
    } else {
      percent = Math.min(99, Math.floor(((today - startDay) / span) * 100));
    }
    const barClass = percent >= 100 ? 'bg-red-500' : percent > 70 ? 'bg-amber-400' : 'bg-emerald-500';
    const daysLeft = Math.round((dueDay - today) / 86_400_000);
    const absDays = Math.abs(daysLeft);
    const daysText = absDays === 1 ? '1 día' : `${absDays} días`;
    const remainingLabel = daysLeft < 0
      ? `Vencido hace ${daysText}`
      : daysLeft === 0
        ? 'Vence hoy'
        : `Vence en ${daysText}`;
    return { start, due, percent, barClass, expired: this.isDueDateExpired(due), remainingLabel };
  }

  /** Etiqueta de la forma de pago del registro de pago (catálogo PaymentForm). */
  getPaymentFormLabel(form: PaymentFormDTO | undefined): string {
    if (!form) return '-';
    const desc = (form.description ?? '').trim();
    if (desc) return desc;
    const name = (form.name ?? '').trim();
    if (name) return name;
    const code = (form.code ?? '').trim();
    return code || '-';
  }

  /** Etiqueta del método de pago del intento (tarjeta, SPEI, etc.). */
  getPaymentAttemptMethodLabel(method: PaymentRecordMethodEnumValue | string | undefined): string {
    if (method == null || String(method).trim() === '') return '-';
    const labels: Record<string, string> = {
      [PaymentRecordMethodEnum.CREDIT_CARD]: 'Tarjeta de crédito',
      [PaymentRecordMethodEnum.DEBIT_CARD]: 'Tarjeta de débito',
      [PaymentRecordMethodEnum.CASH]: 'Efectivo',
      [PaymentRecordMethodEnum.TRANSFER]: 'Transferencia',
      [PaymentRecordMethodEnum.CHECK]: 'Cheque',
      [PaymentRecordMethodEnum.SPEI]: 'SPEI',
      [PaymentRecordMethodEnum.COD]: 'Contra entrega',
      [PaymentRecordMethodEnum.OTHER]: 'Otro'
    };
    return labels[method] ?? method;
  }

  /** Etiqueta del status del intento de pago. */
  getPaymentAttemptStatusLabel(status: PaymentRecordStatusEnumValue | string | undefined): string {
    if (status == null || String(status).trim() === '') return '-';
    const labels: Record<string, string> = {
      [PaymentRecordStatusEnum.PAID]: 'Pagado',
      [PaymentRecordStatusEnum.PENDING]: 'Pendiente',
      [PaymentRecordStatusEnum.FAILED]: 'Fallido',
      [PaymentRecordStatusEnum.CANCELLED]: 'Cancelado',
      [PaymentRecordStatusEnum.EXPIRED]: 'Expirado'
    };
    return labels[status] ?? status;
  }

  frequencyOptions: { value: string; label: string }[] = [
    { value: 'ONCE', label: 'Una sola vez' },
    { value: 'DAILY', label: 'Diario' },
    { value: 'WEEKLY', label: 'Semanal' },
    { value: 'BIWEEKLY', label: 'Quincenal' },
    { value: 'MONTHLY', label: 'Mensual' },
    { value: 'QUARTERLY', label: 'Trimestral' },
    { value: 'SEMIANNUALLY', label: 'Semestral' },
    { value: 'ANNUALLY', label: 'Anual' }
  ];

  /** Opciones de frecuencia para el select. En suscripción no se ofrece «Una sola vez». */
  frequencySelectOptions(): { value: string; label: string; disabled: boolean }[] {
    const opts = this.isSubscriptionSaleType()
      ? this.frequencyOptions.filter((opt) => opt.value !== 'ONCE')
      : this.frequencyOptions;
    return opts.map((opt) => ({ ...opt, disabled: false }));
  }

  /** Suscripción: se infiere de la venta cargada (si tiene suscripción o `saleType=SUBSCRIPTION`). Alta = venta única. */
  isSubscriptionSaleType(): boolean {
    const sale = this.currentSale();
    if (!sale) return false;
    return sale.saleType === 'SUBSCRIPTION' || sale.subscription?.id != null;
  }

  taxCollectionTypeOptions: { value: string; label: string }[] = [
    { value: 'SELLER_COLLECTS', label: 'Traslado' },
    { value: 'SELLER_WITHHOLDS', label: 'Retención' }
  ];

  taxApplicationOptions: { value: TaxApplicationType; label: string }[] = [
    { value: 'TAXABLE', label: 'Gravado' },
    { value: 'EXEMPT', label: 'Exento' },
    { value: 'ZERO_RATED', label: 'Tasa 0%' },
    { value: 'REVERSE_CHARGE', label: 'Inversión del sujeto' }
  ];

  taxExemptOptions = [
    { value: false, label: 'No' },
    { value: true, label: 'Sí' }
  ];

  taxInclusiveHelpText =
    'Indica si el precio unitario ya incluye impuestos. Si está marcado, el importe mostrado ya considera los impuestos y no se sumarán impuestos adicionales sobre el precio.';

  currencySelectOptions(): { id: number; label: string }[] {
    return this.currencyOptions().map((c) => ({
      id: c.id!,
      label: `${c.code} - ${c.name}`
    }));
  }

  /** Código de moneda seleccionada para formatear totales. */
  get currencyCode(): string {
    const id = this.generalForm.get('currencyId')?.value ?? this.currentSale()?.currency;
    const currency = this.currencyOptions().find((c) => c.id === id);
    if (currency?.code) return currency.code;
    if (typeof this.currentSale()?.currency === 'string') return this.currentSale()!.currency!;
    return 'MXN';
  }

  totals = computed(() => {
    const items = this.lines();
    let subtotal = 0;
    let traslados = 0;
    let retenciones = 0;
    items.forEach((l) => {
      subtotal += Number(l.subtotal) || 0;
      (l.taxes ?? []).forEach((t) => {
        const amount = Number(t.taxAmount) || 0;
        if (t.collectionType === 'SELLER_WITHHOLDS') {
          retenciones += amount;
        } else {
          traslados += amount;
        }
      });
    });
    const tax = traslados - retenciones;
    const total = subtotal + tax;
    return { subtotal, tax, total, traslados, retenciones };
  });

  constructor() {
    this.generalForm = this.fb.group({
      customerId: [null, Validators.required],
      customerSelection: [null],
      number: ['', [Validators.maxLength(20)]],
      subtotal: [0, [Validators.required, Validators.min(0)]],
      traslados: [0, [Validators.min(0)]],
      retenciones: [0, [Validators.min(0)]],
      tax: [0, [Validators.required, Validators.min(0)]],
      discount: [0, [Validators.required, Validators.min(0)]],
      total: [0, [Validators.required, Validators.min(0)]],
      currencyId: [null, Validators.required],
      processStatus: [SalePipelineStatusEnum.OC, Validators.required],
      status: [SaleProcessStatusEnum.DRAFT, Validators.required],
      collectionMethod: [CollectionMethodEnum.LINK, Validators.required],
      dateOfSale: [new Date()],
      notes: ['', [Validators.maxLength(500)]]
    });

    merge(this.generalForm.valueChanges, this.generalForm.statusChanges)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.generalTabDraftRefresh.update((n) => n + 1));

    this.invoicingForm = this.fb.group({
      paymentFormId: [null],
      paymentMethodId: [null],
      cfdiUseId: [null],
      autoInvoice: [false]
    });

    this.paymentTermsForm = this.fb.group({
      collectionStartDate: [null],
      daysUntilExpiry: [null],
      penaltyFeeAmount: [null],
      penaltyInterestRate: [null],
      numberOfPayments: [1, [Validators.required, Validators.min(1), Validators.max(1)]],
      frequency: ['ONCE']
    });

    this.additionalFieldsForm = this.fb.group({});

    this.saleForm = this.fb.group({
      general: this.generalForm,
      invoicing: this.invoicingForm,
      paymentTerms: this.paymentTermsForm,
      additionalFields: this.additionalFieldsForm
    });

    merge(this.additionalFieldsForm.valueChanges, this.additionalFieldsForm.statusChanges)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.additionalFieldsRefresh.update((n) => n + 1));

    merge(this.paymentTermsForm.statusChanges, this.paymentTermsForm.valueChanges)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.paymentTermsDraftRefresh.update((n) => n + 1));

    merge(
      this.invoicingForm.statusChanges,
      this.invoicingForm.valueChanges
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.paymentTermsDraftRefresh.update((n) => n + 1));

    merge(
      this.invoicingForm.get('paymentMethodId')!.valueChanges,
      this.invoicingForm.get('paymentFormId')!.valueChanges,
      this.invoicingForm.get('cfdiUseId')!.valueChanges,
      this.invoicingForm.get('autoInvoice')!.valueChanges
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.cdr.markForCheck());

    this.invoicingForm
      .get('autoInvoice')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncInvoicingControlsEnabledState());

    this.paymentTermsForm.get('numberOfPayments')?.valueChanges.subscribe((n) => {
      if (this.isSubscriptionSaleType()) return;
      if (n !== 1) {
        this.paymentTermsForm.get('numberOfPayments')?.patchValue(1, { emitEvent: false });
      }
      this.syncFrequencyFromNumberOfPayments(1);
      this.syncPaymentMethodFromNumberOfPayments(1);
    });
    this.paymentTermsForm.get('frequency')?.disable({ emitEvent: false });

    this.syncInvoicingControlsEnabledState();

    this.lineForm = this.fb.group({
      productSelection: [null],
      productId: [null],
      description: ['', [Validators.required, Validators.maxLength(250)]],
      quantity: [1, [Validators.required, Validators.min(0.0001)]],
      unitPrice: [0, [Validators.required, Validators.min(0)]],
      measureUnitSelection: [null],
      measureUnitId: [null],
      taxProductCodeSelection: [null],
      taxProductCodeId: [null],
      satObjetoImpSelection: [null],
      satObjetoImpId: [null],
      taxExempt: [false],
      taxInclusive: [false],
      measureUnitSearch: [''],
      taxProductCodeSearch: ['']
    });

    merge(
      this.lineForm.get('quantity')!.valueChanges,
      this.lineForm.get('unitPrice')!.valueChanges
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshLineSubtotal());
    this.refreshLineSubtotal();

    this.lineTaxForm = this.fb.group({
      taxSelection: [null, Validators.required],
      taxCode: [''],
      taxRate: [null, [Validators.min(0), Validators.max(100)]],
      taxAmount: [null, [Validators.min(0)]],
      taxBase: ['', [Validators.min(0)]],
      applicationType: ['TAXABLE', Validators.required],
      collectionType: ['SELLER_COLLECTS']
    });
    this.setupLineTaxFormExclusiveFields();

    this.payChargeForm = this.fb.group({
      paymentFormId: [null, Validators.required],
      reference: ['', [Validators.required, Validators.maxLength(50)]],
      bank: ['', [Validators.maxLength(255)]],
      description: ['', [Validators.maxLength(1000)]],
      paymentDate: [null, Validators.required],
      sendReceiptEmail: [true]
    });

    this.generalForm.get('number')?.disable({ emitEvent: false });

    this.loadCurrencies();
    this.loadTaxes();
    this.loadPaymentForms();
    this.loadPaymentMethods();
    this.loadSATCFDIUses();
    this.loadCfdiCancelMotivos();
    this.loadCompanyPaymentTerms();

    this.syncSubscriptionPaymentTermsFromSaleType();

    this.route.paramMap.subscribe((params) => {
      const routeId = params.get('id');
      if (routeId && routeId !== 'nuevo') {
        const id = Number(routeId);
        if (!Number.isNaN(id)) {
          this.isEditMode.set(true);
          this.editingSaleId.set(id);
          this.loadSale(id);
          return;
        }
      }
      this.isEditMode.set(false);
      this.editingSaleId.set(null);
      this.currentSale.set(null);
      this.lines.set([]);
      this.attachments.set([]);
      this.generalForm.patchValue(
        {
          subtotal: 0,
          tax: 0,
          traslados: 0,
          retenciones: 0,
          discount: 0,
          total: 0,
          number: '',
          currencyId: null,
          processStatus: SalePipelineStatusEnum.OC,
          status: SaleProcessStatusEnum.DRAFT,
          collectionMethod: CollectionMethodEnum.LINK,
          dateOfSale: new Date()
        },
        { emitEvent: false }
      );
      this.applyDefaultCurrencyIfNeeded();
      this.invoicingForm.reset({
        paymentFormId: null,
        paymentMethodId: null,
        cfdiUseId: null,
        autoInvoice: false
      });
      this.syncInvoicingControlsEnabledState();
      this.paymentTermsForm.patchValue({
        collectionStartDate: null,
        daysUntilExpiry: null,
        penaltyFeeAmount: null,
        penaltyInterestRate: null,
        numberOfPayments: 1,
        frequency: 'ONCE'
      });
      this.syncSubscriptionPaymentTermsFromSaleType();
      this.syncPaymentMethodFromNumberOfPayments(1);
      this.activeTab.set('general');
      this.generalTabDraftRefresh.update((n) => n + 1);
      this.cdr.markForCheck();
    });

    effect(() => {
      this.currentSale()?.status;
      this.currentSale()?.isStamped;
      this.currentSale()?.invoice;
      this.syncInvoicingControlsEnabledState();
    });

    effect(() => {
      this.editLevel();
      this.canEditAdditionalFields();
      this.syncAdditionalFieldsEnabledState();
      this.syncEditLevelLocks();
    });
  }

  /**
   * Método de pago, forma de pago y uso CFDI habilitados si el perfil de facturación es editable
   * (borrador, venta cerrada sin timbrar). Se bloquean si ya hay CFDI.
   * Con autoInvoice, método de pago y uso de CFDI son obligatorios.
   */
  private syncInvoicingControlsEnabledState(): void {
    const autoInvoiceRequired = !!this.invoicingForm.get('autoInvoice')?.value;
    const canEdit = this.canEditInvoicingPaymentAndCfdiFields();

    const methodCtrl = this.invoicingForm.get('paymentMethodId');
    if (methodCtrl) {
      methodCtrl.clearValidators();
      if (autoInvoiceRequired) {
        methodCtrl.setValidators([Validators.required]);
      }
      if (canEdit) {
        methodCtrl.enable({ emitEvent: false });
      } else {
        methodCtrl.disable({ emitEvent: false });
      }
      methodCtrl.updateValueAndValidity({ emitEvent: false });
    }

    const formCtrl = this.invoicingForm.get('paymentFormId');
    if (formCtrl) {
      formCtrl.clearValidators();
      if (canEdit) {
        formCtrl.enable({ emitEvent: false });
      } else {
        formCtrl.disable({ emitEvent: false });
      }
      formCtrl.updateValueAndValidity({ emitEvent: false });
    }

    const cfdiCtrl = this.invoicingForm.get('cfdiUseId');
    if (cfdiCtrl) {
      cfdiCtrl.clearValidators();
      if (autoInvoiceRequired) {
        cfdiCtrl.setValidators([Validators.required]);
      }
      if (canEdit) {
        cfdiCtrl.enable({ emitEvent: false });
      } else {
        cfdiCtrl.disable({ emitEvent: false });
      }
      cfdiCtrl.updateValueAndValidity({ emitEvent: false });
    }

    const autoInvoiceCtrl = this.invoicingForm.get('autoInvoice');
    if (autoInvoiceCtrl) {
      if (canEdit) {
        autoInvoiceCtrl.enable({ emitEvent: false });
      } else {
        autoInvoiceCtrl.disable({ emitEvent: false });
      }
    }
    this.cdr.markForCheck();
  }

  /** Asteriscos de obligatoriedad cuando autoInvoice está marcado. */
  requiresAutoInvoiceFiscalFields(): boolean {
    return !!this.invoicingForm?.get('autoInvoice')?.value;
  }

  /** Aviso cuando autoInvoice está activo y al cliente le faltan datos para timbrar. */
  autoInvoiceCustomerWarning(): string | null {
    if (!this.requiresAutoInvoiceFiscalFields()) {
      return null;
    }
    const customerId = this.generalForm.get('customerId')?.value as number | null;
    if (customerId == null) {
      return null;
    }
    if (this.invoiceCustomerStatus() === 'loading') {
      return 'Verificando los datos fiscales del cliente…';
    }
    const missing = missingCustomerInvoiceFields(this.invoiceCustomer());
    return missing.length ? customerInvoiceReadinessMessage(missing) : null;
  }

  /** Aviso cuando autoInvoice está activo y algún producto no tiene datos para timbrar. */
  autoInvoiceProductWarning(): string | null {
    if (!this.requiresAutoInvoiceFiscalFields()) {
      return null;
    }
    const messages: string[] = [];
    for (const line of this.lines()) {
      const product = line.product as SaleProductDTO | undefined;
      const catalogProduct = product?.id != null || line.productId != null;
      const name = product?.name?.trim() || line.description?.trim() || 'sin nombre';
      const missing = missingProductInvoiceFields(
        catalogProduct
          ? {
              taxInclusive: product?.taxInclusive,
              measureUnitId: product?.measureUnitId,
              taxCodeId: product?.taxCodeId,
              satObjetoImpId: product?.satObjetoImpId
            }
          : {
              taxInclusive: line.taxInclusive,
              measureUnitId: line.measureUnitId,
              taxCodeId: line.taxProductCodeId,
              satObjetoImpId: line.satObjetoImpId
            }
      );
      if (missing.length) {
        messages.push(productInvoiceReadinessMessage(name, missing));
      }
    }
    return messages.length ? messages.join(' ') : null;
  }

  private autoInvoiceBlockMessage(): string | null {
    const warnings = [this.autoInvoiceCustomerWarning(), this.autoInvoiceProductWarning()].filter(
      (warning): warning is string => !!warning
    );
    return warnings.length ? warnings.join(' ') : null;
  }

  private loadInvoiceCustomer(customerId: number | null | undefined): void {
    if (customerId == null) {
      this.invoiceCustomer.set(null);
      this.invoiceCustomerStatus.set('empty');
      return;
    }
    this.invoiceCustomerStatus.set('loading');
    this.customerService
      .getCustomerById(customerId)
      .pipe(catchError(() => of({ ok: false, body: null as CustomerDTO | null })))
      .subscribe((response) => {
        if (this.generalForm.get('customerId')?.value !== customerId) {
          return;
        }
        this.invoiceCustomer.set(response.ok && response.body ? response.body : null);
        this.invoiceCustomerStatus.set('ready');
        this.cdr.markForCheck();
      });
  }

  /**
   * Auto-selecciona método de pago SAT según número de pagos si aún no hay valor:
   * > 1 → PPD (parcialidades o diferido); en otro caso → PUE (una sola exhibición).
   * No pisa una selección del usuario o de la venta cargada.
   */
  private syncPaymentMethodFromNumberOfPayments(n?: number | null): void {
    const methodCtrl = this.invoicingForm.get('paymentMethodId');
    if (methodCtrl?.value != null) return;
    const num = n !== undefined ? n : this.paymentTermsForm.get('numberOfPayments')?.value;
    const code = typeof num === 'number' && !Number.isNaN(num) && num > 1 ? 'PPD' : 'PUE';
    const match = this.paymentMethodOptions().find((m) => m.code === code);
    if (match?.id == null) return;
    this.invoicingForm.patchValue({ paymentMethodId: match.id }, { emitEvent: false });
    this.cdr.markForCheck();
  }

  /** Dirty solo en los controles que envía PUT /sales/{id}/invoicing. */
  invoicingProfileSubsetDirty(): boolean {
    return !!(
      this.invoicingForm.get('paymentMethodId')?.dirty ||
      this.invoicingForm.get('paymentFormId')?.dirty ||
      this.invoicingForm.get('cfdiUseId')?.dirty ||
      this.invoicingForm.get('autoInvoice')?.dirty
    );
  }

  submitInvoicingProfileUpdate(): void {
    const id = this.editingSaleId();
    if (id == null || this.invoicingProfileUpdateLoading()) return;
    const autoInvoiceWarning = this.autoInvoiceBlockMessage();
    if (autoInvoiceWarning) {
      this.toastr.error(autoInvoiceWarning);
      return;
    }
    const raw = this.invoicingForm.getRawValue();
    this.invoicingProfileUpdateLoading.set(true);
    this.saleService
      .updateInvoicingProfile(id, {
        paymentFormId: raw.paymentFormId ?? null,
        paymentMethodId: raw.paymentMethodId ?? null,
        cfdiUseId: raw.cfdiUseId ?? null,
        autoInvoice: !!raw.autoInvoice
      })
      .pipe(finalize(() => this.invoicingProfileUpdateLoading.set(false)))
      .subscribe({
        next: (r) => {
          if (r.ok && r.body) {
            this.currentSale.set(r.body);
            this.patchSale(r.body);
            this.invoicingForm.markAsPristine();
            this.toastr.success('Perfil de facturación actualizado');
            this.cdr.markForCheck();
          } else {
            this.toastr.error(apiFailureMessage(r, 'No se pudo actualizar el perfil de facturación'));
          }
        },
        error: (err) => {
          this.toastr.error(httpErrorApiMessage(err, 'Error al actualizar el perfil de facturación'));
        }
      });
  }

  /** Regla de frecuencia solo para venta única (nº de pagos > 1 habilita frecuencia). */
  private syncFrequencyFromNumberOfPayments(n: number | null | undefined): void {
    const freqControl = this.paymentTermsForm.get('frequency');
    if (this.isFieldReadonly(SaleEditField.FREQUENCY)) {
      freqControl?.disable({ emitEvent: false });
      freqControl?.updateValueAndValidity({ emitEvent: false });
      return;
    }
    if (n == null || n <= 1) {
      this.paymentTermsForm.patchValue({ frequency: 'ONCE' }, { emitEvent: false });
      freqControl?.disable({ emitEvent: false });
    } else {
      freqControl?.enable({ emitEvent: false });
    }
    freqControl?.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Suscripción: número de pagos deshabilitado (leyenda abierta); frecuencia obligatoria.
   * Venta normal: un solo pago, porque un valor mayor a 1 aún no está implementado.
   */
  private syncSubscriptionPaymentTermsFromSaleType(): void {
    const isSub = this.isSubscriptionSaleType();
    const npCtrl = this.paymentTermsForm.get('numberOfPayments');
    const freqCtrl = this.paymentTermsForm.get('frequency');
    if (!npCtrl || !freqCtrl) return;

    if (isSub) {
      npCtrl.clearValidators();
      npCtrl.patchValue(null, { emitEvent: false });
      npCtrl.disable({ emitEvent: false });
      npCtrl.updateValueAndValidity({ emitEvent: false });
      if (this.isFieldReadonly(SaleEditField.FREQUENCY)) {
        freqCtrl.disable({ emitEvent: false });
      } else {
        freqCtrl.enable({ emitEvent: false });
      }
      freqCtrl.setValidators([Validators.required]);
      if (freqCtrl.value === 'ONCE' || freqCtrl.value == null || freqCtrl.value === '') {
        freqCtrl.patchValue('MONTHLY', { emitEvent: false });
      }
      freqCtrl.updateValueAndValidity({ emitEvent: false });
    } else {
      npCtrl.disable({ emitEvent: false });
      npCtrl.setValidators([Validators.required, Validators.min(1), Validators.max(1)]);
      npCtrl.patchValue(1, { emitEvent: false });
      npCtrl.updateValueAndValidity({ emitEvent: false });
      freqCtrl.clearValidators();
      freqCtrl.updateValueAndValidity({ emitEvent: false });
      this.syncFrequencyFromNumberOfPayments(1);
    }
    this.syncPaymentMethodFromNumberOfPayments();
    this.cdr.markForCheck();
  }

  /** Folio mostrado cuando la venta ya tiene id (`editingSaleId` no null). La etiqueta «Autogenerado» va solo en la plantilla si id es null. */
  folioDisplayValue(): string {
    if (this.loading() && this.editingSaleId() != null) {
      return '…';
    }
    const fromApi = this.currentSale()?.number?.trim();
    if (fromApi) return fromApi;
    const fromForm = this.generalForm.getRawValue().number?.trim();
    return fromForm || '—';
  }

  copyPaymentReference(): void {
    const reference = this.currentSale()?.paymentReference?.trim();
    if (!reference) return;
    void navigator.clipboard.writeText(reference).then(
      () => this.toastr.success('Referencia de pago copiada'),
      () => this.toastr.error('No se pudo copiar la referencia')
    );
  }

  /** Etiqueta legible del `status` (API puede usar `InvoiceStatus` o el ciclo operativo legacy). */
  statusDisplayLabel(): string {
    if (this.loading() && this.editingSaleId() != null) {
      return '…';
    }
    const raw =
      this.generalForm.get('status')?.value ?? this.currentSale()?.status ?? SaleProcessStatusEnum.DRAFT;
    const asStr = String(raw);
    const invoiceLabels: Record<string, string> = {
      [InvoiceStatusEnum.DRAFT]: 'Borrador',
      [InvoiceStatusEnum.ISSUED]: 'Emitida',
      [InvoiceStatusEnum.PARTIALLY_PAID]: 'Parcialmente pagada',
      [InvoiceStatusEnum.PAID]: 'Pagada',
      [InvoiceStatusEnum.VOID]: 'Cancelada',
      [InvoiceStatusEnum.CANCELLED]: 'Cancelada',
      [InvoiceStatusEnum.UNCOLLECTIBLE]: 'Incobrable'
    };
    if (invoiceLabels[asStr]) return invoiceLabels[asStr];
    return this.saleProcessStatusOptions.find((o) => o.value === raw)?.label ?? '—';
  }

  private effectTotals = effect(() => {
    const t = this.totals();
    this.generalForm.patchValue(
      {
        subtotal: t.subtotal,
        tax: t.tax,
        traslados: t.traslados,
        retenciones: t.retenciones,
        discount: 0,
        total: t.total
      },
      { emitEvent: false }
    );
  });

  onCustomerSearch(event: { query: string }): void {
    const q = (event.query ?? '').trim();
    if (q.length < 2) {
      this.customerOptions.set([]);
      return;
    }
    this.customerLoading.set(true);
    this.customerService
      .listCustomers(
        { 'legalName[lk]': q, 'comercialName[lk]': q },
        { page: 0, size: 20 }
      )
      .pipe(
        catchError(() => of({ ok: false, body: [] as CustomerDTO[] })),
        finalize(() => this.customerLoading.set(false))
      )
      .subscribe((r) => {
        const list = r.ok && r.body ? r.body : [];
        this.customerOptions.set(list.map((c) => this.customerWithDisplayLabel(c)));
      });
  }

  onProductSearch(event: { query: string }): void {
    const q = (event.query ?? '').trim();
    if (q.length < 2) {
      this.productOptions.set([]);
      return;
    }
    this.productLoading.set(true);
    this.productService
      .getProducts({ 'name[lk]': q }, { page: 0, size: 20 })
      .pipe(
        catchError(() => of({ ok: false, body: [] as ProductDTO[] })),
        finalize(() => this.productLoading.set(false))
      )
      .subscribe((r) => {
        this.productOptions.set(r.ok && r.body ? r.body : []);
      });
  }

  onMeasureUnitSearch(event: { query: string }): void {
    const q = (event.query ?? '').trim();
    if (q.length < 2) {
      this.measureUnitOptions.set([]);
      return;
    }
    this.measureUnitService
      .listMeasureUnits({ 'name[lk]': q, 'code[lk]': q }, { page: 0, size: 20 })
      .pipe(catchError(() => of({ ok: false, body: [] as MeasureUnitDTO[] })))
      .subscribe((r) => {
        this.measureUnitOptions.set(r.ok && r.body ? r.body : []);
      });
  }

  /** Subtotal del modal de línea: cantidad × precio unitario. */
  private refreshLineSubtotal(): void {
    const qty = Number(this.lineForm.get('quantity')?.value) || 0;
    const price = Number(this.lineForm.get('unitPrice')?.value) || 0;
    this.lineSubtotal.set(qty * price);
  }

  /**
   * Selecciona todo el texto al enfocar cantidad/precio (Tab o click).
   * El flag + mouseup evitan que el click reposicione el caret; setTimeout
   * cubre el caso en que el mouseup ya haya corrido en el input interno.
   */
  private suppressMouseUpSelectClear = false;

  selectAllOnFocus(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    if (!input?.select) {
      return;
    }
    this.suppressMouseUpSelectClear = true;
    input.select();
    setTimeout(() => input.select());
  }

  /** Evita que el mouseup del click deshaga la selección hecha en focus. */
  keepSelectionOnMouseUp(event: Event): void {
    if (!this.suppressMouseUpSelectClear) {
      return;
    }
    event.preventDefault();
    this.suppressMouseUpSelectClear = false;
  }

  onTaxProductCodeSearch(event: { query: string }): void {
    const q = (event.query ?? '').trim();
    if (q.length < 2) {
      this.taxProductCodeOptions.set([]);
      return;
    }
    this.taxProductCodeService
      .listTaxProductCodes({ 'taxCode[lk]': q, 'taxDescription[lk]': q }, { page: 0, size: 20 })
      .pipe(catchError(() => of({ ok: false, body: [] as TaxProductCodeDTO[] })))
      .subscribe((r) => {
        this.taxProductCodeOptions.set(r.ok && r.body ? r.body : []);
      });
  }

  onMeasureUnitSelected(value: MeasureUnitDTO | null): void {
    if (value) {
      this.lineForm.patchValue({ measureUnitId: value.id ?? null, measureUnitSelection: value });
    }
  }

  onTaxProductCodeSelected(value: TaxProductCodeDTO | null): void {
    if (value) {
      this.lineForm.patchValue({ taxProductCodeId: value.id ?? null, taxProductCodeSelection: value });
    }
  }

  loadCurrencies(): void {
    this.currencyLoading.set(true);
    this.currencyService
      .listCurrencies({}, { page: 0, size: 200 })
      .pipe(
        catchError(() => of({ ok: false, body: [] as CurrencyDTO[] })),
        finalize(() => this.currencyLoading.set(false))
      )
      .subscribe((r) => {
        this.currencyOptions.set(r.ok ? r.body : []);
        this.applyDefaultCurrencyIfNeeded();
      });
  }

  /** Moneda por defecto en ventas nuevas: MXN (Peso mexicano). No aplica al editar una venta existente. */
  private applyDefaultCurrencyIfNeeded(): void {
    if (this.editingSaleId() != null) return;
    if (this.generalForm.get('currencyId')?.value != null) return;
    const mxn = this.currencyOptions().find((c) => String(c.code).toUpperCase() === 'MXN');
    if (mxn?.id == null) return;
    this.generalForm.patchValue({ currencyId: mxn.id }, { emitEvent: false });
    this.cdr.markForCheck();
  }

  loadTaxes(): void {
    this.taxService
      .listTaxes({}, { page: 0, size: 200 })
      .pipe(catchError(() => of({ ok: false, body: [] as TaxDTO[] })))
      .subscribe((r) => {
        this.taxOptions.set(r.ok ? r.body : []);
      });
  }

  loadPaymentForms(): void {
    this.paymentFormService
      .listPaymentForms({ state: 'ACTIVE' }, { page: 0, size: 500 })
      .pipe(catchError(() => of({ ok: false, body: [] as PaymentFormDTO[] })))
      .subscribe((r) => {
        this.paymentFormOptions.set(r.ok && r.body ? r.body : []);
      });
  }

  loadPaymentMethods(): void {
    this.paymentMethodService
      .listPaymentMethods({ state: 'ACTIVE' }, { page: 0, size: 500 })
      .pipe(catchError(() => of({ ok: false, body: [] as PaymentMethodDTO[] })))
      .subscribe((r) => {
        this.paymentMethodOptions.set(r.ok && r.body ? r.body : []);
        this.syncPaymentMethodFromNumberOfPayments();
      });
  }

  loadSATCFDIUses(): void {
    this.satCfdiUseService
      .list({ state: 'ACTIVE' }, { page: 0, size: 500 })
      .pipe(catchError(() => of({ ok: false, body: [] as SATCFDIUseDTO[] })))
      .subscribe((r) => {
        this.satCfdiUseOptions.set(r.ok && r.body ? r.body : []);
      });
  }

  loadCfdiCancelMotivos(): void {
    this.cfdiCancelMotivoService
      .list({ active: true }, { page: 0, size: 50, sort: ['order,asc'] })
      .pipe(catchError(() => of({ ok: false, body: [] as CfdiCancelMotivoDTO[] })))
      .subscribe((r) => {
        const motivos = r.ok && r.body ? r.body : [];
        this.cfdiCancelMotivoOptions.set(motivos);
        if (!this.cancelCfdiMotivo()) {
          this.cancelCfdiMotivo.set(this.defaultCancelCfdiMotivo(motivos));
        }
      });
  }

  private defaultCancelCfdiMotivo(motivos: CfdiCancelMotivoDTO[] = this.cfdiCancelMotivoOptions()): string {
    return motivos.find((motivo) => !motivo.requiresRelatedCfdi)?.name
      ?? motivos[0]?.name
      ?? '';
  }

  private loadCompanyPaymentTerms(): void {
    this.companyService
      .getMyCompany()
      .pipe(catchError(() => of({ ok: false, body: null as CompanyDTO | null })))
      .subscribe((r) => {
        const body = r.body;
        if (r.ok !== false && body) {
          this.companyPaymentTerms = {
            daysUntilExpiry: body.daysUntilExpiry ?? null
          };
          this.applyDynamicFieldsFromCompany(
            body,
            this.currentSale()?.additionalFields ?? null
          );
          if (this.currentSale() == null && !this.isReadonly()) {
            this.invoicingForm.patchValue(
              {
                autoInvoice:
                  body.companyConfig?.companyInvoiceConfig?.enableAutoInvoice ?? false
              },
              { emitEvent: false }
            );
          }
        }
        if (this.currentSale() != null || this.isReadonly()) {
          return;
        }
        const sel = this.generalForm.get('customerSelection')?.value as CustomerDTO | null;
        if (sel?.id) {
          this.customerService
            .getCustomerById(sel.id)
            .pipe(catchError(() => of({ ok: false, body: sel })))
            .subscribe((resp) => {
              const cust = resp.ok && resp.body ? resp.body : sel;
              this.applyPaymentTermsDefaultsForCustomer(cust);
            });
        } else {
          this.applyCompanyPaymentTermsOnly();
        }
      });
  }

  private applyDynamicFieldsFromCompany(
    company: CompanyDTO,
    existingValues?: Record<string, unknown> | null
  ): void {
    const fields = sortDynamicFields(
      company.companyConfig?.companyInvoiceConfig?.companySaleConfig?.dynamicFields
    );
    this.dynamicFields.set(fields);
    rebuildAdditionalFieldsForm(this.fb, this.additionalFieldsForm, fields, existingValues);
    this.syncAdditionalFieldsEnabledState();
    this.additionalFieldsRefresh.update((n) => n + 1);
    this.cdr.markForCheck();
  }

  /** Deshabilita campos adicionales cuando el editLevel no lo permite. */
  private syncAdditionalFieldsEnabledState(): void {
    const disabled = !this.canEditAdditionalFields();
    Object.keys(this.additionalFieldsForm.controls).forEach((key) => {
      const ctrl = this.additionalFieldsForm.get(key);
      if (!ctrl) return;
      if (disabled && ctrl.enabled) {
        ctrl.disable({ emitEvent: false });
      } else if (!disabled && ctrl.disabled) {
        ctrl.enable({ emitEvent: false });
      }
    });
  }

  /** Bloquea controles de cabecera/términos según editLevel e inmutables del API. */
  private syncEditLevelLocks(): void {
    const apply = (controlName: string, field: SaleEditFieldName, group: FormGroup = this.generalForm): void => {
      const ctrl = group.get(controlName);
      if (!ctrl) return;
      if (this.isFieldReadonly(field)) {
        ctrl.disable({ emitEvent: false });
      } else if (ctrl.disabled) {
        ctrl.enable({ emitEvent: false });
      }
    };
    apply('customerSelection', SaleEditField.CUSTOMER_ID);
    apply('customerId', SaleEditField.CUSTOMER_ID);
    apply('dateOfSale', SaleEditField.DATE_OF_SALE);
    apply('collectionMethod', SaleEditField.COLLECTION_METHOD);
    apply('currencyId', SaleEditField.CURRENCY_ID);
    apply('processStatus', SaleEditField.PROCESS_STATUS);
    apply('notes', SaleEditField.NOTES);
    apply('collectionStartDate', SaleEditField.COLLECTION_START_DATE, this.paymentTermsForm);
    apply('daysUntilExpiry', SaleEditField.DAYS_UNTIL_EXPIRY, this.paymentTermsForm);
    apply('penaltyFeeAmount', SaleEditField.PENALTY_FEE_AMOUNT, this.paymentTermsForm);
    apply('penaltyInterestRate', SaleEditField.PENALTY_INTEREST_RATE, this.paymentTermsForm);
    const npCtrl = this.paymentTermsForm.get('numberOfPayments');
    if (npCtrl?.enabled) {
      npCtrl.disable({ emitEvent: false });
    }
    if (this.isFieldReadonly(SaleEditField.FREQUENCY)) {
      this.paymentTermsForm.get('frequency')?.disable({ emitEvent: false });
    } else {
      this.syncFrequencyFromNumberOfPayments(this.paymentTermsForm.get('numberOfPayments')?.value);
    }
    this.syncInvoicingControlsEnabledState();
  }

  additionalFieldCtrl(fieldKey: string | undefined) {
    return additionalFieldControl(this.additionalFieldsForm, fieldKey);
  }

  selectOptionsForField(field: DynamicField): { label: string; value: string }[] {
    return (field.options ?? []).map((o) => ({ label: o, value: o }));
  }

  fieldTypeLabel(type: DynamicFieldType | undefined): string {
    return dynamicFieldTypeLabel(type);
  }

  /** Rellena plazo y gracia solo con la empresa (factura nueva sin cliente aún). */
  private applyCompanyPaymentTermsOnly(): void {
    const comp = this.companyPaymentTerms;
    if (!comp) return;
    this.paymentTermsForm.patchValue(
      {
        daysUntilExpiry: comp.daysUntilExpiry ?? null
      },
      { emitEvent: false }
    );
    this.cdr.markForCheck();
  }

  /**
   * Prioridad por campo: cliente → empresa → vacío (null).
   */
  private pickPaymentTerm(
    customerVal: number | null | undefined,
    companyVal: number | null | undefined
  ): number | null {
    if (customerVal !== null && customerVal !== undefined) {
      return customerVal;
    }
    if (companyVal !== null && companyVal !== undefined) {
      return companyVal;
    }
    return null;
  }

  private applyPaymentTermsDefaultsForCustomer(customer: CustomerDTO): void {
    const comp = this.companyPaymentTerms;
    const days = this.pickPaymentTerm(customer.daysUntilExpiry, comp?.daysUntilExpiry ?? null);
    this.paymentTermsForm.patchValue({ daysUntilExpiry: days }, { emitEvent: false });
    this.cdr.markForCheck();
  }

  /** Carga el catálogo SATObjetoImp para el select de líneas. */
  private loadSatObjetoImps(page: number = 0, acc: SATObjetoImpDTO[] = []): void {
    this.satObjetoImpService
      .list({}, { page, size: 200, sort: ['name,asc'] })
      .pipe(
        catchError(() => of({ ok: false, body: [] as SATObjetoImpDTO[], page: undefined })),
        finalize(() => {})
      )
      .subscribe((response) => {
        if (!response.ok) {
          this.satObjetoImpOptions.set([]);
          return;
        }
        const merged = [...acc, ...(response.body ?? [])];
        if (response.page && response.page.currentPage < response.page.totalPages) {
          this.loadSatObjetoImps(response.page.currentPage + 1, merged);
          return;
        }
        this.satObjetoImpOptions.set(merged);
      });
  }

  loadSale(id: number): void {
    this.loading.set(true);
    this.formDataLoading.set(true);
    this.saleService
      .getSaleById(id)
      .pipe(
        finalize(() => {
          this.loading.set(false);
          this.formDataLoading.set(false);
        })
      )
      .subscribe({
        next: (r) => {
          if (r.ok && r.body) {
            this.currentSale.set(r.body);
            this.patchSale(r.body);
            const lineVms: SaleLineVm[] = (r.body.lines ?? []).map((l) => this.lineToVm(l));
            this.lines.set(lineVms);
            this.loadCharges();
          } else {
            this.setError(r.message || 'Error al cargar la factura');
          }
        },
        error: () => {
          this.setError('Error al cargar la factura.');
        }
      });
  }

  /** Recarga la venta desde el API (botón refresh del header). */
  refreshSale(): void {
    const id = this.editingSaleId();
    if (id == null || this.loading()) return;
    this.loadSale(id);
    if (this.activeTab() === 'charges') this.loadCharges();
    if (this.activeTab() === 'cfdis') this.loadInvoices();
  }

  private lineToVm(l: SaleLineDTO): SaleLineVm {
    const taxes: CreateSaleLineTaxRequest[] = (l.taxes ?? []).map((t) => ({
      taxCode: t.tax?.taxKey,
      taxRate: t.taxRate,
      taxAmount: t.taxAmount,
      taxBase: t.taxBase,
      applicationType: toLineTaxApplicationTypeForCreate(t.applicationType as TaxApplicationType),
      collectionType: t.collectionType as 'SELLER_COLLECTS' | 'SELLER_WITHHOLDS' | undefined
    }));
    const product = l.product as ProductDTO | undefined;
    const satObjetoImp = l.satObjetoImp ?? (product?.satObjetoImpId ? { id: product.satObjetoImpId, name: product.satObjetoImpName ?? product.satObjetoImpId } : undefined);
    return {
      id: l.id,
      productId: l.product?.id,
      product: l.product,
      description: l.description ?? '',
      quantity: Number(l.quantity ?? 0),
      unitPrice: Number(l.unitPrice ?? 0),
      subtotal: Number(l.subtotal ?? 0),
      taxAmount: Number(l.taxAmount ?? 0),
      totalAmount: Number(l.totalAmount ?? 0),
      measureUnitId: l.measureUnit?.id,
      measureUnit: l.measureUnit,
      taxProductCodeId: l.taxProductCode?.id,
      taxProductCode: l.taxProductCode,
      satObjetoImpId: satObjetoImp?.id,
      satObjetoImp: satObjetoImp as SATObjetoImpDTO | undefined,
      taxExempt: l.taxExempt,
      taxInclusive: l.taxInclusive,
      taxes
    };
  }

  patchSale(inv: SaleDTO): void {
    this.generalForm.patchValue(
      {
        customerId: inv.customer?.id ?? null,
        customerSelection: inv.customer ? this.customerWithDisplayLabel(inv.customer) : null,
        number: inv.number ?? '',
        processStatus: inv.processStatus ?? SalePipelineStatusEnum.OC,
        status: inv.status ?? SaleProcessStatusEnum.DRAFT,
        // Fuente de verdad: `Sale.collectionMethod` en API; `billingTerms` puede no traerlo o diferir.
        collectionMethod: (inv.collectionMethod ?? inv.billingTerms?.collectionMethod ?? CollectionMethodEnum.LINK) as CollectionMethod,
        dateOfSale: parseApiDateTime(inv.dateOfSale ?? undefined) ?? new Date(),
        subtotal: inv.subtotal ?? 0,
        tax: inv.tax ?? 0,
        discount: inv.discount ?? 0,
        total: inv.total ?? 0,
        currencyId: null,
        notes: inv.notes ?? ''
      },
      { emitEvent: false }
    );
    if (inv.currency && typeof inv.currency === 'string') {
      const cur = this.currencyOptions().find((c) => c.code === inv.currency);
      if (cur) this.generalForm.patchValue({ currencyId: cur.id });
    }
    const isSub = (inv.saleType ?? 'ONE_TIME') === 'SUBSCRIPTION';
    const n = isSub ? null : 1;
    const freq = isSub ? (inv.frequency ?? 'MONTHLY') : 'ONCE';
    this.invoicingForm.patchValue({
      paymentFormId: inv.billingTerms?.paymentForm?.id ?? null,
      paymentMethodId: inv.billingTerms?.paymentMethod?.id ?? null,
      cfdiUseId: inv.billingTerms?.cfdiUse?.id ?? null,
      autoInvoice: inv.autoInvoice ?? false
    });
    this.paymentTermsForm.patchValue({
      collectionStartDate: parseApiDateTime(inv.collectionStartDate ?? undefined),
      daysUntilExpiry: inv.daysUntilExpiry ?? null,
      penaltyFeeAmount: inv.penaltyFeeAmount ?? null,
      penaltyInterestRate: inv.penaltyInterestRate ?? null,
      numberOfPayments: n,
      frequency: freq
    });
    this.syncInvoicingControlsEnabledState();
    this.syncSubscriptionPaymentTermsFromSaleType();
    this.syncPaymentMethodFromNumberOfPayments();
    this.syncEditLevelLocks();
    this.loadInvoiceCustomer(inv.customer?.id);
    this.invoicingForm.markAsPristine();
    this.generalTabDraftRefresh.update((n) => n + 1);
    if (this.dynamicFields().length) {
      rebuildAdditionalFieldsForm(
        this.fb,
        this.additionalFieldsForm,
        this.dynamicFields(),
        inv.additionalFields ?? null
      );
      this.syncAdditionalFieldsEnabledState();
      this.additionalFieldsRefresh.update((n) => n + 1);
    }
    this.cdr.markForCheck();
  }

  onCustomerSelected(c: CustomerDTO | null): void {
    if (!c) return;
    this.generalForm.patchValue({
      customerId: c.id,
      customerSelection: this.customerWithDisplayLabel(c)
    });
    const id = c.id;
    if (id == null) return;
    if (this.isFieldReadonly(this.SaleEditField.CUSTOMER_ID)) {
      this.loadInvoiceCustomer(id);
      return;
    }
    this.invoiceCustomerStatus.set('loading');
    this.customerService
      .getCustomerById(id)
      .pipe(catchError(() => of({ ok: false, body: null as CustomerDTO | null })))
      .subscribe((r) => {
        if (this.generalForm.get('customerId')?.value !== id) {
          return;
        }
        const customer = r.ok && r.body ? r.body : c;
        this.invoiceCustomer.set(customer);
        this.invoiceCustomerStatus.set('ready');
        this.applyPaymentTermsDefaultsForCustomer(customer);
        this.cdr.markForCheck();
      });
  }

  openCustomerDetails(): void {
    const customerId = this.generalForm.get('customerId')?.value as number | null;
    if (!customerId) return;
    this.isCustomerDetailsOpen.set(true);
    this.selectedCustomer.set(null);
    this.customerDetailsLoading.set(true);
    this.customerService.getCustomerById(customerId).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          this.selectedCustomer.set(response.body);
        } else {
          this.toastr.error(response.message || 'No se pudo cargar el cliente');
          this.closeCustomerDetails();
        }
        this.customerDetailsLoading.set(false);
      },
      error: (err) => {
        this.customerDetailsLoading.set(false);
        this.toastr.error('Error al cargar el cliente');
        this.closeCustomerDetails();
        console.error('Error loading customer by id:', err);
      }
    });
  }

  closeCustomerDetails(): void {
    this.isCustomerDetailsOpen.set(false);
    this.selectedCustomer.set(null);
    this.customerDetailsLoading.set(false);
  }

  onCustomerDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeCustomerDetails();
  }

  editCustomerFromModal(): void {
    const customer = this.selectedCustomer();
    if (!customer?.id) return;
    this.closeCustomerDetails();
    this.router.navigate(['/customers', customer.id]);
  }

  openSaleFromCustomer(saleId: number): void {
    if (!saleId) return;
    this.closeCustomerDetails();
    this.router.navigate(['/sales', saleId]);
  }

  /** Texto para el input del autocomplete: "nombre comercial - razón social". */
  displayCustomer(c?: CustomerDTO | null): string {
    if (!c) return '';
    const com = (c.comercialName ?? '').trim();
    const leg = (c.legalName ?? '').trim();
    if (com && leg) return `${com} - ${leg}`;
    return com || leg;
  }

  /** Añade `displayLabel` para `optionLabel` del p-autocomplete (objeto único). */
  private customerWithDisplayLabel(c: CustomerDTO): CustomerDTO & { displayLabel: string } {
    return { ...c, displayLabel: this.displayCustomer(c) };
  }

  displayProduct(p?: ProductDTO | null): string {
    if (!p) return '';
    return `${p.name} (${p.sku || p.id})`;
  }

  displayMeasureUnit(m?: MeasureUnitDTO | null): string {
    if (!m) return '';
    return `${m.code} - ${m.name}`;
  }

  displayTaxProductCode(t?: TaxProductCodeDTO | null): string {
    if (!t) return '';
    return `${t.taxCode} - ${t.taxDescription}`;
  }

  lineFormTaxes = signal<CreateSaleLineTaxRequest[]>([]);

  /** Origen de la línea al crear: catálogo (SKU) vs texto abierto. En edición queda fijo. */
  lineProductSource = signal<'CATALOG' | 'OPEN'>('CATALOG');

  lineProductSourceOptions: { label: string; value: 'CATALOG' | 'OPEN' }[] = [
    { label: 'Producto de catálogo', value: 'CATALOG' },
    { label: 'Producto abierto', value: 'OPEN' }
  ];

  /** Tooltip HTML (pTooltip con escape desactivado). */
  readonly lineProductSourceTooltip =
    '<strong>Producto de catálogo:</strong> eliges un artículo del inventario; se precargan descripción, precio, unidad, impuestos y datos fiscales del producto.<br><br>' +
    '<strong>Producto abierto:</strong> escribes la descripción y montos a mano, sin vincular un SKU. Útil para servicios o conceptos que no están dados de alta como producto.';

  /** PrimeNG emite el cambio; `ngModel` dentro de `formGroup` requiere `standalone` y además este evento es más fiable que `ngModelChange`. */
  onLineProductSourceSelectChange(ev: SelectButtonChangeEvent): void {
    const v = ev.value as 'CATALOG' | 'OPEN' | null | undefined;
    if (v !== 'CATALOG' && v !== 'OPEN') return;
    this.onLineProductSourceChange(v);
  }

  onLineProductSourceChange(mode: 'CATALOG' | 'OPEN'): void {
    if (this.lineModalMode() === 'edit') return;
    const prev = this.lineProductSource();
    this.lineProductSource.set(mode);
    if (mode === 'OPEN' && prev === 'CATALOG') {
      this.lineForm.patchValue({
        productSelection: null,
        productId: null
      });
      this.lineFormTaxes.set([]);
    }
    this.syncLineProductSelectionDisabled();
    this.cdr.markForCheck();
    setTimeout(() => this.focusLineEntryFirstField(), 100);
  }

  /** En línea abierta el producto del catálogo no aplica: control deshabilitado. */
  private syncLineProductSelectionDisabled(): void {
    const ctrl = this.lineForm.get('productSelection');
    if (!ctrl) return;
    if (this.lineProductSource() === 'OPEN') {
      ctrl.disable({ emitEvent: false });
    } else {
      ctrl.enable({ emitEvent: false });
    }
  }

  onProductSelected(p: ProductDTO | null): void {
    if (!p) return;
    const satObjetoImp = p.satObjetoImpId
      ? this.satObjetoImpOptions().find((s) => s.id === p.satObjetoImpId) ?? { id: p.satObjetoImpId, name: p.satObjetoImpName ?? p.satObjetoImpId }
      : null;
    this.lineForm.patchValue({
      productId: p.id,
      productSelection: p,
      description: p.name ?? this.lineForm.get('description')?.value,
      unitPrice: p.unitPrice ?? 0,
      measureUnitSelection: p.measureUnitId
        ? { id: p.measureUnitId, code: p.measureUnitCode ?? '', name: p.measureUnitName ?? '' }
        : null,
      measureUnitId: p.measureUnitId,
      taxProductCodeSelection: p.taxCodeId
        ? {
            id: p.taxCodeId,
            taxCode: p.taxCodeTaxCode ?? '',
            taxDescription: p.taxCodeDescription ?? ''
          }
        : null,
      taxProductCodeId: p.taxCodeId,
      satObjetoImpSelection: satObjetoImp,
      satObjetoImpId: p.satObjetoImpId ?? null
    });
    const taxes: CreateSaleLineTaxRequest[] = (p.taxes ?? []).map((t) => ({
      taxCode: t.tax?.taxKey,
      taxRate: t.taxRate ?? t.tax?.rate,
      taxAmount: t.taxAmount,
      taxBase: 0,
      applicationType: toLineTaxApplicationTypeForCreate((t.applicationType ?? 'TAXABLE') as TaxApplicationType),
      collectionType: t.collectionType ?? 'SELLER_COLLECTS'
    }));
    this.lineFormTaxes.set(taxes);
    this.editingLineTaxIndex.set(null);
  }

  /**
   * Menú del split «Cobro»: acción principal = abrir recibo; desplegable = copiar enlace público del recibo.
   */
  cobroSplitMenuItems = computed((): { label: string; icon: string; command: () => void }[] => [
    {
      label: 'Copiar enlace',
      icon: 'pi pi-copy',
      command: () => this.copyFirstReceiptPublicLink()
    }
  ]);

  /** Menú del split CFDI timbrado: descargas secundarias (el clic principal abre el visor PDF). */
  saleStampSplitMenuItems = computed((): { label: string; icon: string; disabled: boolean; command: () => void }[] => {
    const ld = this.loading();
    const pdf = this.downloadPdfLoading();
    const xml = this.downloadXmlLoading();
    const resend = this.resendCfdiLoading();
    const cancel = this.cancelCfdiLoading();
    const invoice = this.currentSale()?.invoice;
    const canCancel = invoice?.status === CfdiInvoiceStatusEnum.ACTIVE;
    return [
      {
        label: 'Descargar PDF',
        icon: 'pi pi-download',
        disabled: ld || pdf || !invoice?.pdfFileId,
        command: () => this.downloadStampPDF()
      },
      {
        label: 'Descargar XML',
        icon: 'pi pi-code',
        disabled: ld || xml || !invoice?.xmlFileId,
        command: () => this.downloadStampXML()
      },
      {
        label: 'Reenviar CFDI',
        icon: 'pi pi-send',
        disabled: ld || resend || (!invoice?.pdfFileId && !invoice?.xmlFileId),
        command: () => this.openResendCfdiDialog()
      },
      {
        label: 'Cancelar CFDI',
        icon: 'pi pi-times-circle',
        disabled: ld || cancel || !canCancel,
        command: () => this.openCancelCfdiDialog()
      }
    ];
  });

  /**
   * Comparativa UI: mismas acciones que la barra superior, expuestas con `p-menubar` (no sustituye a la barra).
   */
  saleCompareMenubarItems = computed((): MenuItem[] => {
    const ld = this.loading();
    const pdf = this.downloadPdfLoading();
    const pdfView = this.cfdiPdfViewerOpening();
    const xml = this.downloadXmlLoading();
    const ch = this.chargesLoading();
    const copyBusy = this.receiptLinkCopyLoading();
    const stamped = this.currentSale()?.invoice != null || this.currentSale()?.isStamped === true;
    const invId = this.linkedInvoiceId();
    const canEmit = this.canEmitCfdi();
    return [
      {
        label: 'Fiscal',
        icon: 'pi pi-file-check',
        items: [
          {
            label: 'Factura',
            icon: 'pi pi-file',
            disabled: ld,
            tooltip: invId ? `Abrir factura #${invId}` : 'Sin factura vinculada; aviso al pulsar',
            tooltipPosition: 'left',
            command: () => this.openLinkedInvoice()
          },
          {
            label: 'CFDI',
            icon: 'pi pi-file-export',
            disabled: ld || !canEmit,
            command: () => this.emitCfdi()
          },
          {
            label: 'Ver PDF',
            icon: 'pi pi-eye',
            disabled: ld || pdfView || pdf || !stamped,
            command: () => this.openCfdiPdfViewer()
          },
          {
            label: 'XML',
            icon: 'pi pi-code',
            disabled: ld || xml || !stamped,
            command: () => this.downloadStampXML()
          },
          {
            label: 'Reenviar CFDI',
            icon: 'pi pi-send',
            disabled: ld || this.resendCfdiLoading() || !stamped,
            command: () => this.openResendCfdiDialog()
          },
          {
            label: 'Cancelar CFDI',
            icon: 'pi pi-times-circle',
            disabled: ld || this.cancelCfdiLoading() || this.currentSale()?.invoice?.status !== CfdiInvoiceStatusEnum.ACTIVE,
            command: () => this.openCancelCfdiDialog()
          }
        ]
      },
      {
        label: 'Cobro',
        icon: 'pi pi-credit-card',
        items: [
          {
            label: 'Ver recibo',
            icon: 'pi pi-receipt',
            disabled: ld || ch,
            tooltip: 'Primer cargo con link (split y botón Recibo de la barra)',
            tooltipPosition: 'left',
            command: () => this.openFirstReceiptShortcut()
          },
          {
            label: 'Copiar enlace',
            icon: 'pi pi-copy',
            disabled: ld || ch || copyBusy,
            tooltip: 'Copiar al portapapeles el enlace público del recibo (primer cargo con link)',
            tooltipPosition: 'left',
            command: () => this.copyFirstReceiptPublicLink()
          }
        ]
      }
    ];
  });

  /**
   * Envía el formulario con el estado indicado.
   * @param status 'DRAFT' = Crear/Guardar borrador; 'ISSUED' = Cerrar venta (`InvoiceStatus.ISSUED`).
   *                Si se omite en edición parcial, conserva el status actual.
   * @param options.skipFullEditConfirm si true, no pide confirmación de regeneración (ya confirmada).
   */
  onSubmitWithStatus(
    status?: 'DRAFT' | 'ISSUED',
    options?: { skipFullEditConfirm?: boolean }
  ): void {
    const resolvedStatus: 'DRAFT' | 'ISSUED' =
      status ??
      (this.currentSale()?.status === InvoiceStatusEnum.DRAFT || this.currentSale()?.status == null
        ? 'DRAFT'
        : 'ISSUED');

    if (
      !options?.skipFullEditConfirm &&
      this.isEditMode() &&
      this.editLevel() === SaleEditLevelEnum.FULL &&
      this.willRegenerateChargesOnSave()
    ) {
      this.pendingFullEditSaveStatus.set(status);
      this.fullEditSaveConfirmVisible.set(true);
      return;
    }

    // Validar campos básicos usando la lógica de canSaveDraft
    if (!this.canSaveDraft()) {
      this.generalForm.markAllAsTouched();
      this.invoicingForm.markAllAsTouched();
      this.paymentTermsForm.markAllAsTouched();
      this.additionalFieldsForm.markAllAsTouched();
      this.toastr.error(
        'Completa los datos obligatorios: cliente, moneda, términos de pago y facturación.'
      );
      return;
    }

    if (resolvedStatus === 'ISSUED' && this.editLevel() === SaleEditLevelEnum.FULL) {
      // Validaciones estrictas para emitir
      if (this.paymentTermsForm.invalid) {
        this.paymentTermsForm.markAllAsTouched();
        this.toastr.error('Verifica los términos de pago y fechas.');
        return;
      }
      if (this.invoicingForm.invalid) {
        this.invoicingForm.markAllAsTouched();
        this.toastr.error('Verifica los datos de facturación.');
        return;
      }
      if (this.generalForm.invalid) {
        this.generalForm.markAllAsTouched();
        this.toastr.error('Verifica los datos generales de la venta.');
        return;
      }
      if (this.additionalFieldsForm.invalid) {
        this.additionalFieldsForm.markAllAsTouched();
        this.toastr.error('Verifica los campos adicionales.');
        return;
      }
        if (this.lines().length === 0) {
        this.toastr.error('Agrega al menos una línea para cerrar la venta.');
        return;
      }
    }

    const customerId = this.generalForm.get('customerId')?.value;
    const currencyId = this.generalForm.get('currencyId')?.value;
    if (this.editLevel() === SaleEditLevelEnum.FULL && (!customerId || !currencyId)) {
      this.toastr.error('Cliente y moneda son obligatorios.');
      return;
    }

    const autoInvoiceWarning = this.autoInvoiceBlockMessage();
    if (autoInvoiceWarning) {
      this.toastr.error(autoInvoiceWarning);
      return;
    }

    this.loading.set(true);
    let req: CreateSaleRequest;
    try {
      // Para edición parcial (ADDITIONAL_ONLY / FISCAL_DATA) conservar status operativo actual
      const statusForRequest =
        this.editLevel() !== SaleEditLevelEnum.FULL && this.currentSale()?.status
          ? (this.currentSale()!.status as 'DRAFT' | 'ISSUED')
          : resolvedStatus;
      // PARTIALLY_PAID / PAID: buildRequest solo acepta DRAFT|ISSUED; mapear a ISSUED para no degradar
      const buildStatus: 'DRAFT' | 'ISSUED' =
        statusForRequest === 'DRAFT' ? 'DRAFT' : 'ISSUED';
      req = this.buildRequest(buildStatus);
      // Si la venta ya estaba PARTIALLY_PAID/PAID, no degradar el status en el payload
      const currentOp = this.currentSale()?.status;
      if (
        this.editLevel() !== SaleEditLevelEnum.FULL &&
        (currentOp === InvoiceStatusEnum.PARTIALLY_PAID || currentOp === InvoiceStatusEnum.PAID)
      ) {
        req.status = currentOp;
      }
    } catch (e) {
      this.loading.set(false);
      this.toastr.error(e instanceof Error ? e.message : 'Error al preparar la venta');
      return;
    }
    const isEdit = this.isEditMode() && this.editingSaleId();
    const req$ = isEdit
      ? this.saleService.updateSale(this.editingSaleId()!, req)
      : this.saleService.createSale(req);

    req$
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (r) => {
          if (r.ok) {
            const id = r.body?.id;
            if (id) {
              this.isEditMode.set(true);
              this.editingSaleId.set(id);
              this.currentSale.set(r.body!);
              if (r.body?.number != null) {
                this.generalForm.patchValue({ number: r.body.number }, { emitEvent: false });
              }
              this.syncInvoicingControlsEnabledState();
              this.syncAdditionalFieldsEnabledState();
              this.syncEditLevelLocks();
              if (r.body?.lines) {
                this.lines.set(r.body.lines.map((l) => this.lineToVm(l)));
              }
              this.loadCharges();
              this.router.navigate(['/sales', id], { replaceUrl: true });
            }
            this.toastr.success(
              resolvedStatus === 'ISSUED' && this.editLevel() === SaleEditLevelEnum.FULL
                ? 'Venta cerrada correctamente'
                : isEdit
                  ? 'Venta actualizada correctamente'
                  : 'Venta guardada como borrador'
            );
          } else {
            this.setError(r.message || 'Error al guardar la venta');
          }
        },
        error: (err) => {
          const msg = err.error?.message || httpErrorApiMessage(err, 'Error al conectar con el servidor');
          this.setError(msg);
        }
      });
  }

  /** Guardar cambios permitidos por editLevel (notas / fiscales / adicionales). */
  submitAllowedSaleUpdate(): void {
    this.onSubmitWithStatus();
  }

  cancelFullEditSaveConfirm(): void {
    this.fullEditSaveConfirmVisible.set(false);
    this.pendingFullEditSaveStatus.set(undefined);
  }

  onFullEditSaveConfirmVisibleChange(visible: boolean): void {
    this.fullEditSaveConfirmVisible.set(visible);
    if (!visible) {
      this.pendingFullEditSaveStatus.set(undefined);
    }
  }

  confirmFullEditSaveSubmit(): void {
    const status = this.pendingFullEditSaveStatus();
    this.fullEditSaveConfirmVisible.set(false);
    this.pendingFullEditSaveStatus.set(undefined);
    setTimeout(() => this.onSubmitWithStatus(status, { skipFullEditConfirm: true }), 0);
  }

  /**
   * True si el guardado regeneraría cargos/ligas: venta cerrada con cargos existentes
   * y cambio de montos/líneas o de plazos de cobro (espejo de SaleService).
   */
  private willRegenerateChargesOnSave(): boolean {
    const sale = this.currentSale();
    if (!sale) return false;
    const status = sale.status;
    const managed =
      status === InvoiceStatusEnum.ISSUED ||
      status === InvoiceStatusEnum.PARTIALLY_PAID ||
      status === InvoiceStatusEnum.PAID;
    if (!managed) return false;
    if (this.charges().length === 0) return false;
    return this.detectMoneyChanges() || this.detectScheduleChanges();
  }

  private detectMoneyChanges(): boolean {
    const sale = this.currentSale();
    if (!sale) return false;
    const formDiscount = Number(this.generalForm.get('discount')?.value ?? 0);
    const saleDiscount = Number(sale.discount ?? 0);
    if (formDiscount !== saleDiscount) return true;

    const formLines = this.lines();
    const saleLines = sale.lines ?? [];
    const existingById = new Map(
      saleLines.filter((l) => l.id != null).map((l) => [l.id!, l])
    );
    const seenIds = new Set<number>();

    for (const line of formLines) {
      if (line.id == null) return true;
      const existing = existingById.get(line.id);
      if (!existing) return true;
      seenIds.add(line.id);
      const formPayload = lineHashPayload({
        productId: line.productId,
        quantity: line.quantity,
        unitPrice: line.unitPrice,
        taxes: line.taxes
      });
      const existingPayload = lineHashPayload({
        productId: existing.product?.id,
        quantity: existing.quantity,
        unitPrice: existing.unitPrice,
        taxes: (existing.taxes ?? []).map((t) => ({ taxCode: t.tax?.taxKey }))
      });
      if (formPayload !== existingPayload) return true;
    }

    for (const id of existingById.keys()) {
      if (!seenIds.has(id)) return true;
    }
    return false;
  }

  private detectScheduleChanges(): boolean {
    const sale = this.currentSale();
    if (!sale) return false;
    const pt = this.paymentTermsForm.getRawValue();

    const formStart = formValueToApiDateTime(pt.collectionStartDate);
    const saleStart = sale.collectionStartDate ?? undefined;
    if (formStart != null && saleStart != null) {
      const a = formStart.slice(0, 10);
      const b = String(saleStart).slice(0, 10);
      if (a !== b) return true;
    }

    const formDays = pt.daysUntilExpiry != null ? Number(pt.daysUntilExpiry) : null;
    const saleDays = sale.daysUntilExpiry != null ? Number(sale.daysUntilExpiry) : null;
    if (formDays != null && saleDays != null && formDays !== saleDays) return true;

    const formPenalty = pt.penaltyFeeAmount != null ? Number(pt.penaltyFeeAmount) : null;
    const salePenalty = sale.penaltyFeeAmount != null ? Number(sale.penaltyFeeAmount) : null;
    if (formPenalty != null && formPenalty !== salePenalty) return true;

    const formRate = pt.penaltyInterestRate != null ? Number(pt.penaltyInterestRate) : null;
    const saleRate = sale.penaltyInterestRate != null ? Number(sale.penaltyInterestRate) : null;
    if (formRate != null && formRate !== saleRate) return true;

    return false;
  }

  private buildRequest(status: 'DRAFT' | 'ISSUED' = 'DRAFT'): CreateSaleRequest {
    const iv = this.generalForm.getRawValue();
    const inv = this.invoicingForm.getRawValue();
    const pt = this.paymentTermsForm.getRawValue();
    const isSub = this.isSubscriptionSaleType();
    const billingTerms: CreateSaleBillingTerms = {
      billingType: 'PROFORMA',
      collectionMethod: iv.collectionMethod as CollectionMethod,
      cfdiUseId: inv.cfdiUseId != null ? String(inv.cfdiUseId) : undefined
    };

    const lines: CreateSaleLineRequest[] = this.lines().map((l) => {
      const subtotal = Number(l.quantity) * Number(l.unitPrice);
      const taxAmount = l.taxes?.reduce((s, t) => s + (Number(t.taxAmount) || 0), 0) ?? 0;
      const totalAmount = subtotal + taxAmount;
      // Backend exige @PositiveOrZero en taxAmount y totalAmount
      const safeTaxAmount = Math.max(0, Number(l.taxAmount) ?? taxAmount);
      const safeTotalAmount = Math.max(0, Number(l.totalAmount) ?? totalAmount);
      return {
        id: l.id,
        productId: l.productId,
        description: l.description,
        measureUnitId: l.measureUnitId,
        taxProductCodeId: l.taxProductCodeId,
        satObjetoImpId: l.satObjetoImpId,
        quantity: Number(l.quantity),
        unitPrice: Number(l.unitPrice),
        subtotal: Number(l.subtotal),
        taxAmount: safeTaxAmount,
        totalAmount: safeTotalAmount,
        taxExempt: l.taxExempt,
        taxInclusive: l.taxInclusive,
        taxes: l.taxes?.length ? l.taxes : undefined,
        metadata: undefined
      };
    });

    const paymentFormId = inv.paymentFormId != null ? Number(inv.paymentFormId) : undefined;
    const paymentMethodId = inv.paymentMethodId != null ? Number(inv.paymentMethodId) : undefined;
    // Totales desde computed (evita desfase con form); backend exige @PositiveOrZero
    const t = this.totals();
    const subtotal = t.subtotal;
    const tax = Math.max(0, t.tax);
    const total = Math.max(0, t.total);
    const operationalStatusPayload =
      status === 'ISSUED' ? InvoiceStatusEnum.ISSUED : InvoiceStatusEnum.DRAFT;
    const payload: CreateSaleRequest = {
      customerId: Number(iv.customerId),
      billingTerms,
      paymentFormId,
      paymentMethodId,
      processStatus: iv.processStatus ?? this.currentSale()?.processStatus ?? SalePipelineStatusEnum.OC,
      status: operationalStatusPayload,
      collectionStartDate: formValueToApiDateTime(pt.collectionStartDate),
      dateOfSale: formValueToApiDateTime(iv.dateOfSale),
      daysUntilExpiry: pt.daysUntilExpiry != null ? Number(pt.daysUntilExpiry) : undefined,
      penaltyFeeAmount: pt.penaltyFeeAmount,
      penaltyInterestRate: pt.penaltyInterestRate,
      numberOfPayments: isSub ? undefined : 1,
      frequency: isSub ? (pt.frequency ?? 'MONTHLY') : 'ONCE',
      autoInvoice: !!inv.autoInvoice,
      subtotal,
      tax,
      discount: Number(iv.discount ?? 0),
      total,
      currencyId: Number(iv.currencyId),
      notes: iv.notes ? String(iv.notes).trim() : undefined,
      lines: lines.length ? lines : undefined,
      metadata: undefined,
      additionalFields: serializeAdditionalFields(this.dynamicFields(), this.additionalFieldsForm)
    };
    const subscriptionId = this.currentSale()?.subscription?.id;
    if (subscriptionId != null) {
      payload.subscriptionId = subscriptionId;
    }
    if (this.isEditMode() && this.editingSaleId() != null) {
      payload.id = this.editingSaleId()!;
      this.applyEditLevelToUpdatePayload(payload);
    }
    return payload;
  }

  /**
   * El PUT reenvía inmutables con el valor persistido (el API lo acepta) y omite
   * líneas/plazos/perfil CFDI que el nivel de edición rechazaría.
   */
  private applyEditLevelToUpdatePayload(payload: CreateSaleRequest): void {
    const sale = this.currentSale();
    if (!sale) return;
    const level = this.editLevel();

    payload.customerId = sale.customer?.id ?? payload.customerId;
    const persistedMethod = sale.collectionMethod ?? sale.billingTerms?.collectionMethod;
    if (payload.billingTerms && persistedMethod != null) {
      payload.billingTerms.collectionMethod = persistedMethod as CollectionMethod;
    }
    if (sale.numberOfPayments != null) {
      payload.numberOfPayments = sale.numberOfPayments;
    }
    if (sale.frequency != null) {
      payload.frequency = sale.frequency;
    }
    if (sale.subscription?.id != null) {
      payload.subscriptionId = sale.subscription.id;
    }

    if (level !== SaleEditLevelEnum.FULL) {
      // null explícito: el API distingue "no enviar líneas" de lista vacía (borrar todas).
      payload.lines = null;
      payload.discount = sale.discount ?? 0;
      payload.subtotal = sale.subtotal ?? payload.subtotal;
      payload.tax = sale.tax ?? payload.tax;
      payload.total = sale.total ?? payload.total;
      payload.collectionStartDate = undefined;
      payload.daysUntilExpiry = undefined;
      payload.penaltyFeeAmount = undefined;
      payload.penaltyInterestRate = undefined;
      const cur =
        typeof sale.currency === 'string'
          ? this.currencyOptions().find((c) => c.code === sale.currency)
          : undefined;
      if (cur?.id != null) {
        payload.currencyId = cur.id;
      }
    }

    if (level === SaleEditLevelEnum.ADDITIONAL_ONLY) {
      payload.dateOfSale = undefined;
      payload.paymentFormId = sale.billingTerms?.paymentForm?.id;
      payload.paymentMethodId = sale.billingTerms?.paymentMethod?.id;
      payload.autoInvoice = sale.autoInvoice ?? false;
      const cfdiId = sale.billingTerms?.cfdiUse?.id;
      if (payload.billingTerms) {
        payload.billingTerms.cfdiUseId = cfdiId != null ? String(cfdiId) : undefined;
      }
    }
  }

  cancel(): void {
    this.router.navigate(['/sales']);
  }

  emitCfdi(): void {
    this.toastr.info('El timbrado CFDI aplica solo a facturas, no a ventas.');
  }

  loadCharges(): void {
    const id = this.editingSaleId();
    if (id == null) return;
    this.chargesLoading.set(true);
    this.expandedChargeIds.set(new Set());
    this.chargeAttemptsById.set({});
    this.chargeAttemptsLoadingIds.set(new Set());
    this.saleService
      .getSaleCharges(id)
      .pipe(finalize(() => this.chargesLoading.set(false)))
      .subscribe({
        next: (r) => this.charges.set(r.ok && r.body ? r.body : []),
        error: () => this.charges.set([])
      });
  }

  loadInvoices(): void {
    const id = this.editingSaleId();
    if (id == null) return;
    this.invoicesLoading.set(true);
    this.invoiceService
      .listInvoices({ saleId: id }, { page: 0, size: 100, sort: ['issueDate,desc'] })
      .pipe(finalize(() => this.invoicesLoading.set(false)))
      .subscribe({
        next: (r) => this.invoices.set(r.ok && r.body ? r.body : []),
        error: () => this.invoices.set([])
      });
  }

  getInvoiceSerieFolio(invoice: InvoiceDTO): string {
    const serie = invoice.serie?.trim() ?? '';
    const folio = invoice.folio?.trim() ?? '';
    const joined = `${serie}${folio}`;
    return joined || '-';
  }

  getCfdiStatusLabel(status: string | undefined): string {
    switch (status) {
      case CfdiInvoiceStatusEnum.STAMPING:
        return 'Timbrando';
      case CfdiInvoiceStatusEnum.ACTIVE:
        return 'Vigente';
      case CfdiInvoiceStatusEnum.CANCELLATION_IN_PROGRESS:
        return 'En proceso de cancelación';
      case CfdiInvoiceStatusEnum.CANCELLED:
        return 'Cancelado';
      default:
        return status?.trim() ? status : '-';
    }
  }

  getCancelMotivoLabel(invoice: InvoiceDTO): string {
    const code = invoice.cancelMotivo?.trim();
    if (!code) return '-';
    return this.cfdiCancelMotivoSelectOptions().find((option) => option.value === code)?.label ?? code;
  }

  openInvoiceActionsMenu(event: Event, invoice: InvoiceDTO): void {
    const pdfBusy = this.cfdiPdfViewerOpening() || this.downloadPdfLoading();
    const xmlBusy = this.downloadXmlLoading();
    const resendBusy = this.resendCfdiLoading();
    const cancelBusy = this.cancelCfdiLoading();
    const canCancel = invoice.status === CfdiInvoiceStatusEnum.ACTIVE;
    this.invoiceRowMenuItems.set([
      {
        label: 'Previsualizar PDF',
        icon: 'pi pi-eye',
        disabled: !invoice.pdfFileId || pdfBusy,
        command: () => this.openCfdiPdfViewer(invoice)
      },
      {
        label: 'Descargar PDF',
        icon: 'pi pi-file-pdf',
        disabled: !invoice.pdfFileId || this.downloadPdfLoading(),
        command: () => this.downloadStampPDF(invoice)
      },
      {
        label: 'Descargar XML',
        icon: 'pi pi-code',
        disabled: !invoice.xmlFileId || xmlBusy,
        command: () => this.downloadStampXML(invoice)
      },
      {
        label: 'Reenviar CFDI',
        icon: 'pi pi-send',
        disabled: resendBusy || (!invoice.pdfFileId && !invoice.xmlFileId),
        command: () => this.openResendCfdiDialog(invoice)
      },
      {
        label: 'Cancelar CFDI',
        icon: 'pi pi-times-circle',
        disabled: cancelBusy || !canCancel,
        command: () => this.openCancelCfdiDialog(invoice)
      }
    ]);
    this.invoiceActionsMenu?.toggle(event);
  }

  isChargeExpanded(chargeId: number | undefined): boolean {
    if (chargeId == null) return false;
    return this.expandedChargeIds().has(chargeId);
  }

  isChargeAttemptsLoading(chargeId: number | undefined): boolean {
    if (chargeId == null) return false;
    return this.chargeAttemptsLoadingIds().has(chargeId);
  }

  getChargeAttempts(chargeId: number | undefined): PaymentAttemptDTO[] {
    if (chargeId == null) return [];
    return this.chargeAttemptsById()[chargeId] ?? [];
  }

  /**
   * Expande/colapsa la fila del recibo. Los intentos se consultan solo al expandir
   * (y se cachean hasta refrescar la lista de cargos).
   */
  toggleChargeExpanded(charge: ChargeDTO): void {
    const chargeId = charge.id;
    if (chargeId == null) return;

    const wasExpanded = this.expandedChargeIds().has(chargeId);
    this.expandedChargeIds.update((set) => {
      const next = new Set(set);
      if (next.has(chargeId)) next.delete(chargeId);
      else next.add(chargeId);
      return next;
    });

    if (wasExpanded) return;
    if (this.chargeAttemptsById()[chargeId] != null) return;
    this.loadChargeAttempts(chargeId);
  }

  private loadChargeAttempts(chargeId: number): void {
    this.chargeAttemptsLoadingIds.update((set) => {
      const next = new Set(set);
      next.add(chargeId);
      return next;
    });
    this.chargeService
      .listPaymentAttempts(chargeId)
      .pipe(
        finalize(() => {
          this.chargeAttemptsLoadingIds.update((set) => {
            const next = new Set(set);
            next.delete(chargeId);
            return next;
          });
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (r) => {
          this.chargeAttemptsById.update((map) => ({
            ...map,
            [chargeId]: r.ok && r.body ? r.body : []
          }));
        },
        error: (err) => {
          this.toastr.error(httpErrorApiMessage(err, 'Error al cargar los intentos de pago'));
        }
      });
  }

  /**
   * Abre el menú overlay de acciones del recibo (Reenviar liga, Reintentar cargo, Pagar).
   */
  openChargeActionsMenu(event: Event, charge: ChargeDTO): void {
    const canRecall = this.canSendPaymentLink(charge);
    const canRetry =
      charge.id != null &&
      charge.collectionMethod === CollectionMethodEnum.RECURRING_CHARGE &&
      this.isChargeRetryableStatus(charge.status);
    const canPay = this.canManuallyPayCharge(charge);
    const blockingInstallment = this.pendingPreviousInstallment(charge);
    const canEditPayment = this.canEditManualPayment(charge);
    this.chargeRowMenuItems.set([
      {
        label: 'Seguimiento',
        icon: 'pi pi-chart-line',
        command: () => this.openSeguimientoDialog(charge)
      },
      {
        label: 'Enviar liga de pago',
        icon: 'pi pi-envelope',
        disabled: !canRecall || this.recallLoading(),
        command: () => this.openRecallEmailDialog(charge)
      },
      {
        label: 'Reintentar Cargo',
        icon: 'pi pi-refresh',
        disabled: !canRetry || this.chargeCaptureLoadingId() === charge.id,
        command: () => this.retryCharge(charge)
      },
      {
        label: 'Pagar',
        icon: 'pi pi-wallet',
        disabled: !canPay || this.payChargeLoading(),
        title: blockingInstallment
          ? `Primero registra el pago de la parcialidad ${blockingInstallment.paymentNumber}`
          : undefined,
        command: () => this.openPayChargeDialog(charge)
      },
      {
        label: 'Editar pago',
        icon: 'pi pi-pencil',
        disabled: !canEditPayment || this.payChargeLoading(),
        command: () => this.openEditPayChargeDialog(charge)
      },
      {
        label: 'Cancelar pago',
        icon: 'pi pi-ban',
        disabled: !this.canCancelAppliedPayment(charge) || this.cancelPaymentLoading(),
        command: () => this.openCancelPaymentDialog(charge)
      }
    ]);
    this.chargeActionsMenu?.toggle(event);
  }

  private canSendPaymentLink(charge: ChargeDTO): boolean {
    if (charge.id == null || !this.canManuallyPayCharge(charge)) return false;
    return (
      charge.collectionMethod === CollectionMethodEnum.LINK ||
      charge.collectionMethod === CollectionMethodEnum.MANUAL
    );
  }

  private canManuallyPayCharge(charge: ChargeDTO): boolean {
    if (charge.id == null) return false;
    const payable =
      charge.status === ChargeOperationalStatusEnum.SCHEDULED ||
      charge.status === ChargeOperationalStatusEnum.PENDING ||
      charge.status === ChargeOperationalStatusEnum.DUE ||
      charge.status === ChargeOperationalStatusEnum.FAILED;
    return payable && this.pendingPreviousInstallment(charge) == null;
  }

  /**
   * Primera parcialidad anterior aún abierta; las parcialidades se pagan en orden.
   */
  private pendingPreviousInstallment(charge: ChargeDTO): ChargeDTO | null {
    const paymentNumber = charge.paymentNumber;
    if (paymentNumber == null || paymentNumber <= 1) return null;
    const closed: string[] = [
      ChargeOperationalStatusEnum.PAID,
      ChargeOperationalStatusEnum.CANCELLED,
      ChargeOperationalStatusEnum.EXPIRED
    ];
    const previous = this.charges()
      .filter(
        (c) =>
          c.id !== charge.id &&
          c.state !== 'INACTIVE' &&
          c.paymentNumber != null &&
          c.paymentNumber < paymentNumber &&
          !closed.includes(String(c.status))
      )
      .sort((a, b) => (a.paymentNumber ?? 0) - (b.paymentNumber ?? 0));
    return previous[0] ?? null;
  }

  /**
   * Permite editar un pago ya registrado si la venta es cobro manual y no está facturada.
   */
  canEditManualPayment(charge: ChargeDTO): boolean {
    if (charge.id == null || charge.payment == null) return false;
    const sale = this.currentSale();
    if (sale?.collectionMethod !== CollectionMethodEnum.MANUAL) return false;
    if (sale.isStamped === true) return false;
    if (charge.status !== ChargeOperationalStatusEnum.PAID) return false;
    const provider = charge.payment.paymentProvider;
    return provider == null || provider === PaymentProviderEnum.PENTO;
  }

  private isChargeRetryableStatus(status: string | undefined): boolean {
    return (
      status === ChargeOperationalStatusEnum.PENDING ||
      status === ChargeOperationalStatusEnum.DUE ||
      status === ChargeOperationalStatusEnum.FAILED ||
      status === ChargeOperationalStatusEnum.EXPIRED
    );
  }

  /**
   * POST /charges/{id}/retry: reintento manual de cobro recurrente.
   */
  retryCharge(charge: ChargeDTO): void {
    if (charge.id == null) return;
    if (
      charge.collectionMethod !== CollectionMethodEnum.RECURRING_CHARGE ||
      !this.isChargeRetryableStatus(charge.status)
    ) {
      this.toastr.info('Solo se puede reintentar cargos recurrentes en estado Pendiente, Vencido, Fallido o Expirado.');
      return;
    }
    const chargeId = charge.id;
    this.confirmationService.confirm({
      message: '¿Deseas reintentar el cobro de este recibo con la tarjeta asociada a la suscripción?',
      header: 'Reintentar Cargo',
      icon: 'pi pi-refresh',
      acceptLabel: 'Reintentar',
      rejectLabel: 'Cancelar',
      accept: () => {
        this.chargeCaptureLoadingId.set(chargeId);
        this.chargeService
          .retryRecurringCharge(chargeId)
          .pipe(finalize(() => this.chargeCaptureLoadingId.set(null)))
          .subscribe({
            next: (r) => {
              if (r.ok && r.body) {
                const updated = r.body;
                this.charges.update((list) =>
                  list.map((c) => (c.id === updated.id ? { ...c, ...updated } : c))
                );
                if (this.isChargeExpanded(chargeId)) {
                  this.loadChargeAttempts(chargeId);
                }
                const paid = updated.status === ChargeOperationalStatusEnum.PAID;
                this.toastr.success(
                  r.message ?? (paid ? 'Cobro procesado correctamente' : 'Reintento de cobro procesado')
                );
                this.cdr.markForCheck();
              } else {
                this.toastr.error(apiFailureMessage(r, 'No se pudo reintentar el cobro'));
              }
            },
            error: (err) => {
              this.toastr.error(httpErrorApiMessage(err, 'Error al reintentar el cobro'));
            }
          });
      }
    });
  }

  openRecallEmailDialog(charge: ChargeDTO): void {
    if (charge.id == null) return;
    const defaultEmail =
      charge.paymentLink?.customer?.email ??
      this.currentSale()?.customer?.email ??
      '';
    this.recallChargeId.set(charge.id);
    this.recallEmail.set(defaultEmail);
    this.recallConvertsManualCharge.set(charge.collectionMethod === CollectionMethodEnum.MANUAL);
    this.recallEmailDialogVisible.set(true);
  }

  closeRecallEmailDialog(): void {
    if (this.recallLoading()) return;
    this.recallEmailDialogVisible.set(false);
    this.recallChargeId.set(null);
    this.recallEmail.set('');
    this.recallConvertsManualCharge.set(false);
  }

  submitRecallEmail(): void {
    const chargeId = this.recallChargeId();
    const email = this.recallEmail().trim();
    const convertingManual = this.recallConvertsManualCharge();
    if (chargeId == null) return;
    if (!email) {
      this.toastr.error('Indica el correo electrónico al que se enviará la liga.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.toastr.error('El correo electrónico no es válido.');
      return;
    }
    this.recallLoading.set(true);
    this.chargeService
      .recall(chargeId, email)
      .pipe(finalize(() => this.recallLoading.set(false)))
      .subscribe({
        next: (r) => {
          if (r.ok) {
            if (r.body) {
              const updated = r.body;
              this.charges.update((list) =>
                list.map((c) => (c.id === updated.id ? { ...c, ...updated } : c))
              );
            }
            this.toastr.success(
              r.message ??
                (convertingManual
                  ? 'Liga de pago creada y correo enviado'
                  : 'Correo de liga de pago enviado')
            );
            this.recallEmailDialogVisible.set(false);
            this.recallChargeId.set(null);
            this.recallEmail.set('');
            this.recallConvertsManualCharge.set(false);
            this.cdr.markForCheck();
          } else {
            this.toastr.error(apiFailureMessage(r, 'No se pudo enviar el correo'));
          }
        },
        error: (err) => {
          this.toastr.error(httpErrorApiMessage(err, 'Error al enviar el correo'));
        }
      });
  }

  openResendCfdiDialog(invoice?: InvoiceDTO): void {
    const invoiceId = this.resolveStampInvoiceId(invoice);
    if (invoiceId == null) return;
    this.resendCfdiInvoiceId.set(invoiceId);
    this.resendCfdiEmail.set(this.currentSale()?.customer?.email ?? '');
    this.resendCfdiDialogVisible.set(true);
  }

  closeResendCfdiDialog(): void {
    if (this.resendCfdiLoading()) return;
    this.resendCfdiDialogVisible.set(false);
    this.resendCfdiEmail.set('');
    this.resendCfdiInvoiceId.set(null);
  }

  submitResendCfdi(): void {
    const invoiceId = this.resendCfdiInvoiceId() ?? this.resolveStampInvoiceId();
    const email = this.resendCfdiEmail().trim();
    if (invoiceId == null) return;
    if (!email) {
      this.toastr.error('Indica el correo electrónico al que se enviará el CFDI.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.toastr.error('El correo electrónico no es válido.');
      return;
    }
    this.resendCfdiLoading.set(true);
    this.invoiceService
      .resendCfdi(invoiceId, email)
      .pipe(finalize(() => this.resendCfdiLoading.set(false)))
      .subscribe({
        next: (r) => {
          if (r.ok) {
            this.toastr.success(r.message ?? 'CFDI reenviado correctamente');
            this.resendCfdiDialogVisible.set(false);
            this.resendCfdiEmail.set('');
            this.resendCfdiInvoiceId.set(null);
          } else {
            this.toastr.error(apiFailureMessage(r, 'No se pudo reenviar el CFDI'));
          }
        },
        error: (err) => {
          this.toastr.error(httpErrorApiMessage(err, 'Error al reenviar el CFDI'));
        }
      });
  }

  openCancelCfdiDialog(invoice?: InvoiceDTO): void {
    const invoiceId = this.resolveStampInvoiceId(invoice);
    const status = invoice?.status ?? this.currentSale()?.invoice?.status;
    if (invoiceId == null || status !== CfdiInvoiceStatusEnum.ACTIVE) return;
    this.cancelCfdiInvoiceId.set(invoiceId);
    this.cancelCfdiMotivo.set(this.defaultCancelCfdiMotivo());
    this.cancelCfdiFolioSustitucion.set('');
    this.cancelCfdiDialogVisible.set(true);
  }

  closeCancelCfdiDialog(): void {
    if (this.cancelCfdiLoading()) return;
    this.cancelCfdiDialogVisible.set(false);
    this.cancelCfdiInvoiceId.set(null);
    this.cancelCfdiFolioSustitucion.set('');
  }

  onCancelCfdiMotivoChange(motivo: string): void {
    this.cancelCfdiMotivo.set(motivo);
    if (!this.selectedCancelCfdiMotivo()?.requiresRelatedCfdi) {
      this.cancelCfdiFolioSustitucion.set('');
    }
  }

  submitCancelCfdi(): void {
    const invoiceId = this.cancelCfdiInvoiceId() ?? this.resolveStampInvoiceId();
    const saleId = this.editingSaleId();
    const motivo = this.cancelCfdiMotivo();
    const folioSustitucion = this.cancelCfdiFolioSustitucion().trim();
    const requiresRelatedCfdi = !!this.selectedCancelCfdiMotivo()?.requiresRelatedCfdi;
    if (invoiceId == null) return;
    if (!motivo) {
      this.toastr.error('Selecciona el motivo de cancelación.');
      return;
    }
    if (requiresRelatedCfdi) {
      if (!folioSustitucion) {
        this.toastr.error('Indica el UUID del CFDI que sustituye.');
        return;
      }
      if (!/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(folioSustitucion)) {
        this.toastr.error('El UUID de sustitución no es válido.');
        return;
      }
    }
    this.cancelCfdiLoading.set(true);
    this.invoiceService
      .cancelCfdi(invoiceId, {
        motivo,
        folioSustitucion: requiresRelatedCfdi ? folioSustitucion : undefined
      })
      .pipe(
        switchMap((r) => {
          if (!r.ok) {
            return throwError(() => new Error(apiFailureMessage(r, 'No se pudo cancelar el CFDI')));
          }
          if (saleId == null) {
            return of(r);
          }
          return this.saleService.getSaleById(saleId).pipe(
            switchMap((saleResp) => {
              if (saleResp.ok && saleResp.body) {
                this.currentSale.set(saleResp.body);
                this.patchSale(saleResp.body);
              }
              return of(r);
            })
          );
        }),
        finalize(() => this.cancelCfdiLoading.set(false))
      )
      .subscribe({
        next: (r) => {
          if (r.ok) {
            this.toastr.success(r.message ?? 'CFDI cancelado correctamente');
            this.cancelCfdiDialogVisible.set(false);
            this.cancelCfdiInvoiceId.set(null);
            this.cancelCfdiFolioSustitucion.set('');
            this.loadInvoices();
            this.cdr.markForCheck();
          } else {
            this.toastr.error(apiFailureMessage(r, 'No se pudo cancelar el CFDI'));
          }
        },
        error: (err) => {
          this.toastr.error(httpErrorApiMessage(err, 'Error al cancelar el CFDI'));
        }
      });
  }

  openPayChargeDialog(charge: ChargeDTO): void {
    if (charge.id == null || !this.canManuallyPayCharge(charge)) return;
    this.payChargeId.set(charge.id);
    this.payChargeMode.set('create');
    this.payChargeScheduledStart.set(
      charge.status === ChargeOperationalStatusEnum.SCHEDULED ? (charge.collectionStartDate ?? null) : null
    );
    const paymentDateDefault = new Date();
    paymentDateDefault.setHours(0, 0, 0, 0);
    this.payChargeForm.reset({
      paymentFormId: null,
      reference: '',
      bank: '',
      description: '',
      paymentDate: paymentDateDefault,
      sendReceiptEmail: true
    });
    this.payChargeDialogVisible.set(true);
  }

  openEditPayChargeDialog(charge: ChargeDTO): void {
    if (charge.id == null || !this.canEditManualPayment(charge) || charge.payment == null) return;
    this.payChargeId.set(charge.id);
    this.payChargeMode.set('edit');
    this.payChargeScheduledStart.set(null);
    const payment = charge.payment;
    this.payChargeForm.reset({
      paymentFormId: payment.paymentForm?.id ?? null,
      reference: payment.providerId ?? '',
      bank: payment.bank ?? '',
      description: payment.resultMessage ?? '',
      paymentDate: parseApiDateTime(payment.paidAt ?? charge.paidAt) ?? new Date(),
      sendReceiptEmail: false
    });
    this.payChargeDialogVisible.set(true);
  }

  closePayChargeDialog(): void {
    if (this.payChargeLoading()) return;
    this.payChargeDialogVisible.set(false);
    this.payChargeId.set(null);
    this.payChargeMode.set('create');
    this.payChargeScheduledStart.set(null);
  }

  submitPayCharge(): void {
    const chargeId = this.payChargeId();
    if (chargeId == null) return;
    if (this.payChargeForm.invalid) {
      this.payChargeForm.markAllAsTouched();
      this.toastr.error('Completa todos los campos del pago.');
      return;
    }
    const raw = this.payChargeForm.getRawValue();
    const paymentDate = formValueToApiInstant(raw.paymentDate);
    if (!paymentDate) {
      this.toastr.error('La fecha de pago no es válida.');
      return;
    }
    const isEdit = this.payChargeMode() === 'edit';
    const payload = {
      paymentFormId: Number(raw.paymentFormId),
      reference: String(raw.reference ?? '').trim(),
      bank: String(raw.bank ?? '').trim() || undefined,
      description: String(raw.description ?? '').trim() || undefined,
      paymentDate,
      sendReceiptEmail: raw.sendReceiptEmail === true
    };
    this.payChargeLoading.set(true);
    const request$ = isEdit
      ? this.chargeService.updatePayment(chargeId, payload)
      : this.chargeService.applyPayment(chargeId, payload);
    request$
      .pipe(finalize(() => this.payChargeLoading.set(false)))
      .subscribe({
        next: (r) => {
          if (r.ok && r.body) {
            this.toastr.success(
              r.message ?? (isEdit ? 'Pago actualizado correctamente' : 'Pago aplicado correctamente')
            );
            this.payChargeDialogVisible.set(false);
            this.payChargeId.set(null);
            this.payChargeMode.set('create');
            this.payChargeScheduledStart.set(null);
            if (this.isChargeExpanded(chargeId)) {
              this.chargeAttemptsById.update((map) => {
                const next = { ...map };
                delete next[chargeId];
                return next;
              });
            }
            this.refreshSale();
            if (this.isChargeExpanded(chargeId)) {
              this.loadChargeAttempts(chargeId);
            }
            this.cdr.markForCheck();
          } else {
            this.toastr.error(
              apiFailureMessage(r, isEdit ? 'No se pudo actualizar el pago' : 'No se pudo aplicar el pago')
            );
          }
        },
        error: (err) => {
          this.toastr.error(
            httpErrorApiMessage(err, isEdit ? 'Error al actualizar el pago' : 'Error al aplicar el pago')
          );
        }
      });
  }

  onTabChange(value: string | number | undefined): void {
    const tab = String(value ?? '');
    const id = this.editingSaleId();
    if ((tab === 'charges' || tab === 'cfdis' || tab === 'attachments' || tab === 'auditoria') && !id) return;
    this.activeTab.set(tab);
    if (tab === 'charges') this.loadCharges();
    if (tab === 'cfdis') this.loadInvoices();
    if (tab === 'attachments') this.loadAttachments();
  }

  openSeguimientoDialog(charge: ChargeDTO): void {
    if (charge.id == null) {
      return;
    }
    this.seguimientoDialogChargeId.set(charge.id);
    this.seguimientoDialogSubtitle.set(this.buildChargeSeguimientoSubtitle(charge));
    this.seguimientoDialogVisible.set(true);
  }

  onSeguimientoDialogVisibleChange(visible: boolean): void {
    this.seguimientoDialogVisible.set(visible);
    if (!visible) {
      this.seguimientoDialogChargeId.set(null);
      this.seguimientoDialogSubtitle.set(null);
    }
  }

  private buildChargeSeguimientoSubtitle(charge: ChargeDTO): string {
    if (charge.paymentNumber != null && charge.numberOfPayments != null) {
      return `Recibo de pago ${charge.paymentNumber}/${charge.numberOfPayments}`;
    }
    return 'Recibo de pago';
  }

  saleCustomerDisplayName(): string {
    const customer = this.currentSale()?.customer;
    if (!customer) return '';
    return (customer.comercialName || customer.legalName || customer.email || '').trim();
  }

  /** Abre el formulario de factura vinculada si el API expone `invoiceId` o viene en cargos. */
  openLinkedInvoice(): void {
    const id = this.linkedInvoiceId();
    if (id == null) {
      this.toastr.info(
        'Aún no hay factura vinculada. Si la venta genera una factura fiscal, aparecerá aquí cuando el sistema la asocie.'
      );
      return;
    }
    void this.router.navigate(['/invoices', id]);
  }

  /** Abre el detalle de la suscripción vinculada a esta venta. */
  openLinkedSubscription(): void {
    const id = this.linkedSubscriptionId();
    if (id == null) {
      this.toastr.info('Esta venta no tiene una suscripción vinculada.');
      return;
    }
    this.openSubscriptionDetails(id);
  }

  openSubscriptionDetails(subscriptionId: number): void {
    if (!subscriptionId) return;
    this.isSubscriptionDetailsOpen.set(true);
    this.selectedSubscription.set(null);
    this.subscriptionDetailsLoading.set(true);
    this.subscriptionService.getSubscriptionById(subscriptionId).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          this.selectedSubscription.set(response.body);
        } else {
          this.toastr.error(response.message || 'No se pudo cargar la suscripción');
          this.closeSubscriptionDetails();
        }
        this.subscriptionDetailsLoading.set(false);
      },
      error: (err) => {
        this.subscriptionDetailsLoading.set(false);
        this.toastr.error('Error al cargar la suscripción');
        this.closeSubscriptionDetails();
        console.error('Error loading subscription by id:', err);
      }
    });
  }

  closeSubscriptionDetails(): void {
    this.isSubscriptionDetailsOpen.set(false);
    this.selectedSubscription.set(null);
    this.subscriptionDetailsLoading.set(false);
  }

  onSubscriptionDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeSubscriptionDetails();
  }

  editSubscriptionFromModal(): void {
    const subscription = this.selectedSubscription();
    if (!subscription?.id) return;
    this.closeSubscriptionDetails();
    this.router.navigate(['/subscriptions', subscription.id]);
  }

  openSaleFromSubscription(saleId: number): void {
    if (!saleId) return;
    this.closeSubscriptionDetails();
    if (this.editingSaleId() === saleId) return;
    this.router.navigate(['/sales', saleId]);
  }

  linkedSubscriptionStatusLabel(): string {
    const status = this.linkedSubscriptionStatus();
    switch (status) {
      case 'PENDING_PAYMENT':
        return 'Pendiente de pago';
      case 'ACTIVE':
        return 'Activa';
      case 'PAUSED':
        return 'Pausada';
      case 'CANCELLED':
        return 'Cancelada';
      case 'EXPIRED':
        return 'Expirada';
      default:
        return status ?? '—';
    }
  }

  /**
   * Intenta abrir el recibo público del primer cargo con link; si no hay datos, recarga cargos y reintenta.
   */
  openFirstReceiptShortcut(): void {
    const tryOpen = (): boolean => {
      const ch = this.firstChargeWithPaymentLink();
      if (ch) {
        this.openChargeReceipt(ch);
        return true;
      }
      return false;
    };
    if (tryOpen()) return;
    const saleId = this.editingSaleId();
    if (saleId == null) return;
    this.chargesLoading.set(true);
    this.saleService
      .getSaleCharges(saleId)
      .pipe(finalize(() => this.chargesLoading.set(false)))
      .subscribe({
        next: (r) => {
          this.charges.set(r.ok && r.body ? r.body : []);
          this.cdr.markForCheck();
          if (!tryOpen()) {
            this.toastr.info('No hay enlaces de recibo disponibles. Revisa la pestaña Recibos de pago.');
          }
        },
        error: () => {
          this.charges.set([]);
          this.toastr.error('No se pudieron cargar los recibos.');
        }
      });
  }

  /** Primer cargo con link de pago e id (para recibo / copiar enlace). */
  private firstChargeWithPaymentLink(): ChargeDTO | undefined {
    return this.charges().find((c) => Boolean(c.paymentLink?.link && c.id));
  }

  /**
   * Copia al portapapeles la URL pública del recibo del primer cargo con link (misma ruta que al abrir en pestaña).
   */
  copyFirstReceiptPublicLink(): void {
    const copyForCharge = (ch: ChargeDTO): void => {
      if (!ch?.id) return;
      this.receiptLinkCopyLoading.set(true);
      this.invoiceService
        .createChargePublicHash(ch.id, 30)
        .pipe(
          finalize(() => {
            this.receiptLinkCopyLoading.set(false);
            this.cdr.markForCheck();
          })
        )
        .subscribe({
          next: (r) => {
            if (r.ok && r.body) {
              const baseUrl = window.location.origin;
              const url = `${baseUrl}/public/charges/${ch.id}/receipt?hash=${encodeURIComponent(r.body)}`;
              void navigator.clipboard.writeText(url).then(
                () => this.toastr.success('Enlace del recibo copiado al portapapeles'),
                () => this.toastr.error('No se pudo copiar el enlace')
              );
            } else {
              this.toastr.error(r.message ?? 'No se pudo generar el enlace del recibo');
            }
          },
          error: (err) => {
            this.toastr.error(err.error?.message ?? 'Error al generar el enlace del recibo');
          }
        });
    };

    const tryCopy = (): boolean => {
      const ch = this.firstChargeWithPaymentLink();
      if (ch) {
        copyForCharge(ch);
        return true;
      }
      return false;
    };

    if (tryCopy()) return;
    const saleId = this.editingSaleId();
    if (saleId == null) {
      this.toastr.info('No hay enlaces de recibo disponibles. Revisa la pestaña Recibos de pago.');
      return;
    }
    this.chargesLoading.set(true);
    this.saleService
      .getSaleCharges(saleId)
      .pipe(finalize(() => this.chargesLoading.set(false)))
      .subscribe({
        next: (r) => {
          this.charges.set(r.ok && r.body ? r.body : []);
          this.cdr.markForCheck();
          if (!tryCopy()) {
            this.toastr.info('No hay enlaces de recibo disponibles. Revisa la pestaña Recibos de pago.');
          }
        },
        error: () => {
          this.charges.set([]);
          this.toastr.error('No se pudieron cargar los recibos.');
        }
      });
  }

  openPaymentLink(url: string): void {
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  }

  /**
   * Genera un hash público del cargo y abre la página de recibo público.
   */
  openChargeReceipt(charge: ChargeDTO): void {
    if (!charge?.id) return;
    this.loading.set(true);
    this.invoiceService
      .createChargePublicHash(charge.id, 30)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (r) => {
          if (r.ok && r.body) {
            const baseUrl = window.location.origin;
            const url = `${baseUrl}/public/charges/${charge.id}/receipt?hash=${encodeURIComponent(r.body)}`;
            window.open(url, '_blank', 'noopener,noreferrer');
            this.toastr.success('Recibo público abierto en nueva pestaña');
          } else {
            this.toastr.error(r.message ?? 'No se pudo generar el hash del recibo');
          }
        },
        error: (err) => {
          this.toastr.error(err.error?.message ?? 'Error al generar el recibo');
        }
      });
  }

  /** URL usable en ventana o iframe a partir de `ReadFileResponse.content` (URL, data: o base64). */
  private normalizeStampPdfSrc(content: string): string {
    const t = content.trim();
    if (t.startsWith('http://') || t.startsWith('https://') || t.startsWith('blob:') || t.startsWith('data:')) {
      return t;
    }
    return `data:application/pdf;base64,${t}`;
  }

  private stampPdfContentToSafeUrl(content: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(this.normalizeStampPdfSrc(content));
  }

  private resolveStampInvoiceId(invoice?: InvoiceDTO): number | null {
    if (invoice?.id != null) return invoice.id;
    const active = this.invoices().find(
      (inv) => inv.status === CfdiInvoiceStatusEnum.ACTIVE && inv.id != null
    );
    if (active?.id != null) return active.id;
    return this.linkedInvoiceId();
  }

  /** Abre el diálogo con visor embebido del PDF del CFDI. */
  openCfdiPdfViewer(invoice?: InvoiceDTO): void {
    const id = this.resolveStampInvoiceId(invoice);
    if (id == null || this.cfdiPdfViewerOpening()) return;
    this.cfdiPdfViewerOpening.set(true);
    this.invoiceService
      .getStampPDF(id)
      .pipe(finalize(() => this.cfdiPdfViewerOpening.set(false)))
      .subscribe({
        next: (r) => {
          if (r.ok && r.body?.content) {
            this.cfdiPdfViewerSafeUrl.set(this.stampPdfContentToSafeUrl(r.body.content));
            this.cfdiPdfViewerVisible.set(true);
            this.cdr.markForCheck();
          } else {
            this.toastr.error(apiFailureMessage(r, 'No se pudo obtener el PDF del CFDI'));
          }
        },
        error: (err) => {
          this.toastr.error(httpErrorApiMessage(err, 'Error al obtener el PDF'));
        }
      });
  }

  onCfdiPdfViewerVisibleChange(visible: boolean): void {
    this.cfdiPdfViewerVisible.set(visible);
    if (!visible) {
      this.cfdiPdfViewerSafeUrl.set(null);
    }
  }

  private sanitizeDownloadFilename(name: string, defaultExt: string): string {
    let s = name.replace(/[/\\?%*:|"<>]/g, '_').trim() || `CFDI${defaultExt}`;
    if (!/\.\w{2,8}$/i.test(s)) {
      s += defaultExt.startsWith('.') ? defaultExt : `.${defaultExt}`;
    }
    return s;
  }

  private triggerBrowserDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  downloadStampPDF(invoice?: InvoiceDTO): void {
    const id = this.resolveStampInvoiceId(invoice);
    if (id == null) return;
    this.downloadPdfLoading.set(true);
    this.invoiceService
      .downloadStampPdfFile(id)
      .pipe(
        finalize(() => {
          this.downloadPdfLoading.set(false);
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: ({ blob, filename }) => {
          const name = this.sanitizeDownloadFilename(filename, '.pdf');
          this.triggerBrowserDownload(blob, name);
          this.toastr.success('Descarga del PDF iniciada');
        },
        error: (err) => {
          this.toastr.error(httpErrorApiMessage(err, 'Error al descargar el PDF'));
        }
      });
  }

  downloadStampXML(invoice?: InvoiceDTO): void {
    const id = this.resolveStampInvoiceId(invoice);
    if (id == null) return;
    this.downloadXmlLoading.set(true);
    this.invoiceService
      .downloadStampXmlFile(id)
      .pipe(
        finalize(() => {
          this.downloadXmlLoading.set(false);
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: ({ blob, filename }) => {
          const name = this.sanitizeDownloadFilename(filename, '.xml');
          this.triggerBrowserDownload(blob, name);
          this.toastr.success('Descarga del XML iniciada');
        },
        error: (err) => {
          this.toastr.error(httpErrorApiMessage(err, 'Error al descargar el XML'));
        }
      });
  }

  /** Abre el diálogo de confirmación antes de timbrar (Facturar). */
  confirmStampSale(): void {
    if (this.loading() || this.stampSaleLoading() || !this.canStampSaleCfdi() || this.editingSaleId() == null) {
      return;
    }
    this.stampFiscalSummary.set(this.buildStampFiscalSummary());
    this.stampSaleConfirmVisible.set(true);
  }

  cancelStampSaleConfirm(): void {
    this.stampSaleConfirmVisible.set(false);
    this.stampPaymentForm99WarningVisible.set(false);
    this.stampFiscalSummary.set(null);
  }

  onStampSaleConfirmVisibleChange(visible: boolean): void {
    this.stampSaleConfirmVisible.set(visible);
    if (!visible && !this.stampPaymentForm99WarningVisible()) {
      this.stampFiscalSummary.set(null);
    }
  }

  /**
   * Tras confirmar el resumen: si la forma de pago es «99 - Por definir»,
   * muestra un segundo aviso; si no, timbra de inmediato.
   */
  proceedStampSaleAfterSummary(): void {
    if (this.stampSaleLoading()) return;
    if (this.isPaymentFormPorDefinir()) {
      this.stampPaymentForm99WarningVisible.set(true);
      this.stampSaleConfirmVisible.set(false);
      return;
    }
    this.executeStampSale();
  }

  cancelStampPaymentForm99Warning(): void {
    this.stampPaymentForm99WarningVisible.set(false);
    this.stampFiscalSummary.set(null);
  }

  onStampPaymentForm99WarningVisibleChange(visible: boolean): void {
    this.stampPaymentForm99WarningVisible.set(visible);
    if (!visible) this.stampFiscalSummary.set(null);
  }

  confirmStampPaymentForm99Warning(): void {
    if (this.stampSaleLoading()) return;
    this.stampPaymentForm99WarningVisible.set(false);
    this.executeStampSale();
  }

  /** Forma de pago SAT «99 - Por definir» (código 99). */
  private isPaymentFormPorDefinir(): boolean {
    const sale = this.currentSale();
    const bt = sale?.billingTerms;
    const raw = this.invoicingForm.getRawValue() as { paymentFormId?: number | null };
    const paymentForm =
      bt?.paymentForm ??
      this.paymentFormOptions().find((f) => f.id === raw.paymentFormId) ??
      null;
    if (!paymentForm) return false;
    const code = (paymentForm.code ?? '').trim();
    if (code === '99') return true;
    const label = (paymentForm.description ?? paymentForm.name ?? '').trim().toLowerCase();
    return label.startsWith('99') || label.includes('por definir');
  }

  /** Resumen de receptor y términos CFDI que se usarán al timbrar. */
  private buildStampFiscalSummary(): StampFiscalSummary {
    const sale = this.currentSale();
    const customer =
      sale?.customer ??
      (this.generalForm.get('customerSelection')?.value as CustomerDTO | null) ??
      null;
    const bt = sale?.billingTerms;
    const raw = this.invoicingForm.getRawValue() as {
      paymentMethodId?: number | null;
      paymentFormId?: number | null;
      cfdiUseId?: string | null;
    };

    const paymentMethod =
      bt?.paymentMethod ??
      this.paymentMethodOptions().find((m) => m.id === raw.paymentMethodId) ??
      null;
    const paymentForm =
      bt?.paymentForm ??
      this.paymentFormOptions().find((f) => f.id === raw.paymentFormId) ??
      null;
    const cfdiUse =
      bt?.cfdiUse ??
      this.satCfdiUseOptions().find((u) => u.id === raw.cfdiUseId) ??
      null;

    const currency = this.currencyCode;
    const total = sale?.total;
    const totalLabel =
      total != null && Number.isFinite(total)
        ? new Intl.NumberFormat('es-MX', { style: 'currency', currency: currency || 'MXN' }).format(total)
        : '—';

    return {
      legalName: (customer?.legalName ?? '').trim() || '—',
      comercialName: (customer?.comercialName ?? '').trim() || '—',
      taxId: (customer?.taxId ?? '').trim() || '—',
      taxRegime: this.formatSatCatalogLabel(customer?.taxRegime),
      address: this.formatCustomerFiscalAddress(customer),
      email: (customer?.email ?? '').trim() || '—',
      paymentMethod: this.formatSatCatalogLabel(paymentMethod),
      paymentForm: this.formatSatCatalogLabel(paymentForm),
      cfdiUse: this.formatSatCatalogLabel(cfdiUse),
      totalLabel
    };
  }

  /** Misma etiqueta que los selects de Facturación (`optionLabel="description"`). */
  private formatSatCatalogLabel(
    item?: { id?: string | number; code?: string; description?: string; name?: string } | null
  ): string {
    if (!item) return '—';
    const desc = (item.description ?? '').trim();
    if (desc) return desc;
    const name = (item.name ?? '').trim();
    if (name) return name;
    const code = (item.code ?? (typeof item.id === 'string' ? item.id : '')).trim();
    return code || '—';
  }

  private pickCustomerFiscalAddress(customer?: CustomerDTO | null): CustomerAddressDTO | null {
    const addresses = customer?.addresses ?? [];
    if (!addresses.length) return null;
    const billing = addresses.find((a) => a.type === AddressTypeEnum.BILLING);
    const primary = addresses.find((a) => a.type === AddressTypeEnum.PRIMARY);
    const withZip = addresses.find((a) => a.zipCode?.trim());
    return billing ?? primary ?? withZip ?? addresses[0] ?? null;
  }

  private formatCustomerFiscalAddress(customer?: CustomerDTO | null): string {
    const address = this.pickCustomerFiscalAddress(customer);
    if (!address) return '—';
    const parts = [
      address.street?.trim(),
      address.externalNo?.trim() ? `Ext. ${address.externalNo.trim()}` : '',
      address.internalNo?.trim() ? `Int. ${address.internalNo.trim()}` : '',
      address.colonia?.trim(),
      address.municipality?.trim(),
      address.zipCode?.trim() ? `C.P. ${address.zipCode.trim()}` : '',
      address.country?.name?.trim()
    ].filter((part) => !!part && part.length > 0);
    return parts.length ? parts.join(', ') : '—';
  }

  /** POST /sales/{id}/stamp y recarga la venta (actualiza `isStamped`). Cierra el diálogo solo si todo OK. */
  executeStampSale(): void {
    const id = this.editingSaleId();
    if (id == null || !this.canStampSaleCfdi() || this.stampSaleLoading()) return;
    this.stampSaleLoading.set(true);
    this.saleService
      .stampSale(id)
      .pipe(
        switchMap((r) => {
          if (!r.ok) {
            return throwError(() => new Error(apiFailureMessage(r, 'No se pudo timbrar el CFDI')));
          }
          return this.saleService.getSaleById(id);
        }),
        finalize(() => this.stampSaleLoading.set(false))
      )
      .subscribe({
        next: (r) => {
          if (r.ok && r.body) {
            this.stampSaleConfirmVisible.set(false);
            this.stampPaymentForm99WarningVisible.set(false);
            this.stampFiscalSummary.set(null);
            this.currentSale.set(r.body);
            this.patchSale(r.body);
            const lineVms: SaleLineVm[] = (r.body.lines ?? []).map((l) => this.lineToVm(l));
            this.lines.set(lineVms);
            this.syncInvoicingControlsEnabledState();
            this.toastr.success('CFDI de la venta timbrado correctamente');
            this.loadInvoices();
            this.cdr.markForCheck();
          } else {
            this.toastr.error(apiFailureMessage(r, 'Error al recargar la venta'));
          }
        },
        error: (err: unknown) => {
          if (err instanceof Error && err.message.trim()) {
            this.toastr.error(err.message);
            return;
          }
          this.toastr.error(httpErrorApiMessage(err, 'Error al timbrar el CFDI'));
        }
      });
  }

  copyPaymentLink(url: string): void {
    if (!url) return;
    navigator.clipboard.writeText(url).then(
      () => this.toastr.success('Link de pago copiado al portapapeles'),
      () => this.toastr.error('No se pudo copiar el link')
    );
  }

  openLineModal(mode: 'create' | 'edit', index?: number): void {
    if (!this.canEditLines()) {
      this.toastr.warning(saleEditLevelTooltip(this.editLevel()));
      return;
    }
    this.loadSatObjetoImps();
    this.lineModalMode.set(mode);
    this.editingLineIndex.set(index ?? null);
    this.lineFiscalDetailsExpanded.set(false);
    if (mode === 'create') {
      this.lineProductSource.set('CATALOG');
      this.lineForm.reset({
        productSelection: null,
        productId: null,
        description: '',
        quantity: 1,
        unitPrice: 0,
        measureUnitSelection: null,
        measureUnitId: null,
        taxProductCodeSelection: null,
        taxProductCodeId: null,
        satObjetoImpSelection: null,
        satObjetoImpId: null,
        taxExempt: false,
        taxInclusive: false,
        measureUnitSearch: '',
        taxProductCodeSearch: ''
      });
    } else if (index !== undefined) {
      const line = this.lines()[index];
      if (line) {
        this.lineProductSource.set(line.productId != null || line.product != null ? 'CATALOG' : 'OPEN');
        const satObjetoImp = line.satObjetoImp ?? (line.satObjetoImpId ? this.satObjetoImpOptions().find((s) => s.id === line.satObjetoImpId) ?? { id: line.satObjetoImpId, name: '' } : null);
        this.lineForm.patchValue({
          productSelection: line.product,
          productId: line.productId,
          description: line.description,
          quantity: line.quantity,
          unitPrice: line.unitPrice,
          measureUnitSelection: line.measureUnit,
          measureUnitId: line.measureUnitId,
          taxProductCodeSelection: line.taxProductCode,
          taxProductCodeId: line.taxProductCodeId,
          satObjetoImpSelection: satObjetoImp,
          satObjetoImpId: line.satObjetoImpId ?? null,
          taxExempt: line.taxExempt ?? false,
          taxInclusive: line.taxInclusive ?? false
        });
        this.lineFormTaxes.set(line.taxes ?? []);
      }
    }
    this.syncLineProductSelectionDisabled();
    this.lineModalOpen.set(true);
    setTimeout(() => this.lineModalShown.set(true), 10);
    this.cdr.detectChanges();
    setTimeout(() => this.focusLineEntryFirstField(), 100);
  }

  private focusLineEntryFirstField(retry = true): void {
    if (this.lineProductSource() === 'OPEN') {
      this.focusLineDescriptionField(retry);
      return;
    }
    this.focusProductField(retry);
  }

  private focusLineDescriptionField(retry = true): void {
    this.cdr.detectChanges();
    const el = document.getElementById('lineModalDescription');
    if (el && 'focus' in el) {
      (el as HTMLInputElement).focus({ preventScroll: false });
    } else if (retry) {
      setTimeout(() => this.focusLineDescriptionField(false), 250);
    }
  }

  private focusProductField(retry = true): void {
    this.cdr.detectChanges();
    const el = this.productLineRef?.nativeElement ?? document.getElementById('productLine');
    const input = el?.querySelector?.('input') as HTMLInputElement | null;
    if (input?.focus) {
      input.focus({ preventScroll: false });
    } else if (retry) {
      setTimeout(() => this.focusProductField(false), 250);
    }
  }

  confirmRemoveLine(index: number): void {
    if (!this.canEditLines()) {
      this.toastr.warning(saleEditLevelTooltip(this.editLevel()));
      return;
    }
    this.confirmationService.confirm({
      message: '¿Eliminar esta línea de la factura?',
      header: 'Confirmar eliminación',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, eliminar',
      rejectLabel: 'Cancelar',
      accept: () => {
        setTimeout(() => {
          this.removeLine(index);
        }, 0);
      }
    });
  }

  /** Abre el diálogo de confirmación antes de cerrar la venta (ISSUED). */
  confirmCloseSale(): void {
    if (this.loading() || !this.canEditLines() || !this.footerDraftSubmitEnabled()) {
      return;
    }
    this.closeSaleConfirmVisible.set(true);
  }

  cancelCloseSaleConfirm(): void {
    this.closeSaleConfirmVisible.set(false);
  }

  confirmCloseSaleSubmit(): void {
    this.closeSaleConfirmVisible.set(false);
    // Cerrar venta ya tuvo su propio diálogo; no repetir el de regeneración FULL.
    setTimeout(() => this.onSubmitWithStatus('ISSUED', { skipFullEditConfirm: true }), 0);
  }

  /**
   * Permite cancelar un pago aplicado si el cobro es manual y la venta no está facturada ni cancelada.
   */
  canCancelAppliedPayment(charge: ChargeDTO): boolean {
    if (charge.id == null || charge.status !== ChargeOperationalStatusEnum.PAID) return false;
    if (this.isSaleCancelled() || this.isSaleInactive()) return false;
    const sale = this.currentSale();
    if (sale?.isStamped === true) return false;
    if (sale?.collectionMethod !== CollectionMethodEnum.MANUAL) return false;
    if (charge.collectionMethod !== CollectionMethodEnum.MANUAL) return false;
    return true;
  }

  confirmCancelSale(): void {
    if (this.isSaleCancelled() || this.isSaleInactive() || this.isMerchantInitiatedSale() || this.editingSaleId() == null || this.loading() || this.cancelSaleLoading()) {
      return;
    }
    this.cancelSaleError.set(null);
    this.cancelSaleReason.set('');
    this.cancelPaymentsConfirmVisible.set(false);
    this.cancelSaleConfirmVisible.set(true);
    if (this.charges().length === 0) {
      this.loadCharges();
    }
  }

  cancelCancelSaleConfirm(): void {
    if (this.cancelSaleLoading()) return;
    this.cancelSaleConfirmVisible.set(false);
    this.cancelPaymentsConfirmVisible.set(false);
    this.cancelSaleError.set(null);
    this.cancelSaleReason.set('');
  }

  continueCancelSale(): void {
    if (this.cancelSaleLoading()) return;
    this.cancelSaleError.set(null);
    if (this.cancelSaleReason().length > this.cancelReasonMaxLength) {
      this.cancelSaleError.set(`El motivo de cancelación no puede superar ${this.cancelReasonMaxLength} caracteres`);
      return;
    }
    if (this.hasAppliedPayments()) {
      this.cancelSaleConfirmVisible.set(false);
      this.cancelPaymentsConfirmVisible.set(true);
      return;
    }
    this.executeCancelSale();
  }

  backToCancelSaleConfirm(): void {
    if (this.cancelSaleLoading()) return;
    this.cancelPaymentsConfirmVisible.set(false);
    this.cancelSaleConfirmVisible.set(true);
  }

  onCancelPaymentsDialogVisibleChange(visible: boolean): void {
    if (visible) {
      this.cancelPaymentsConfirmVisible.set(true);
      return;
    }
    if (this.cancelSaleLoading()) return;
    this.backToCancelSaleConfirm();
  }

  executeCancelSale(): void {
    const id = this.editingSaleId();
    if (id == null || this.cancelSaleLoading()) return;

    this.cancelSaleError.set(null);
    this.cancelSaleLoading.set(true);
    const cancelReason = this.cancelSaleReason().trim();
    this.saleService
      .cancelSale(id, cancelReason ? { cancelReason } : {})
      .pipe(finalize(() => this.cancelSaleLoading.set(false)))
      .subscribe({
        next: (r) => {
          if (r.ok) {
            this.cancelSaleConfirmVisible.set(false);
            this.cancelPaymentsConfirmVisible.set(false);
            this.cancelSaleError.set(null);
            this.cancelSaleReason.set('');
            this.toastr.success(r.message ?? 'Venta cancelada correctamente');
            this.loadSale(id);
            this.loadCharges();
            this.cdr.markForCheck();
          } else {
            this.cancelSaleError.set(apiFailureMessage(r, 'No se pudo cancelar la venta'));
          }
        },
        error: (err) => {
          this.cancelSaleError.set(httpErrorApiMessage(err, 'Error al cancelar la venta'));
        }
      });
  }

  openCancelPaymentDialog(charge: ChargeDTO): void {
    if (!this.canCancelAppliedPayment(charge) || this.cancelPaymentLoading()) return;
    this.cancelPaymentCharge.set(charge);
    this.cancelPaymentReason.set('');
    this.cancelPaymentError.set(null);
    this.cancelPaymentDialogVisible.set(true);
  }

  closeCancelPaymentDialog(): void {
    if (this.cancelPaymentLoading()) return;
    this.cancelPaymentDialogVisible.set(false);
    this.cancelPaymentCharge.set(null);
    this.cancelPaymentReason.set('');
    this.cancelPaymentError.set(null);
  }

  executeCancelPayment(): void {
    const charge = this.cancelPaymentCharge();
    if (charge?.id == null || this.cancelPaymentLoading()) return;
    if (this.cancelPaymentReason().length > this.cancelReasonMaxLength) {
      this.cancelPaymentError.set(`El motivo de cancelación no puede superar ${this.cancelReasonMaxLength} caracteres`);
      return;
    }

    this.cancelPaymentError.set(null);
    this.cancelPaymentLoading.set(true);
    const cancelReason = this.cancelPaymentReason().trim();
    const chargeId = charge.id;
    this.chargeService
      .cancelPayment(chargeId, cancelReason ? { cancelReason } : {})
      .pipe(finalize(() => this.cancelPaymentLoading.set(false)))
      .subscribe({
        next: (r) => {
          if (r.ok && r.body) {
            const updated = r.body;
            this.charges.update((list) =>
              list.map((c) => (c.id === updated.id ? { ...c, ...updated } : c))
            );
            this.cancelPaymentDialogVisible.set(false);
            this.cancelPaymentCharge.set(null);
            this.cancelPaymentReason.set('');
            this.cancelPaymentError.set(null);
            this.toastr.success(r.message ?? 'Pago cancelado correctamente');
            const saleId = this.editingSaleId();
            if (saleId != null) {
              this.loadSale(saleId);
            }
            if (this.isChargeExpanded(chargeId)) {
              this.loadChargeAttempts(chargeId);
            }
            this.cdr.markForCheck();
          } else {
            this.cancelPaymentError.set(apiFailureMessage(r, 'No se pudo cancelar el pago'));
          }
        },
        error: (err) => {
          this.cancelPaymentError.set(httpErrorApiMessage(err, 'Error al cancelar el pago'));
        }
      });
  }

  confirmDeleteSale(): void {
    if (!this.canDeleteSale() || this.editingSaleId() == null || this.loading() || this.deleteSaleLoading()) {
      return;
    }
    this.deleteSaleError.set(null);
    this.deleteSaleConfirmVisible.set(true);
  }

  cancelDeleteSaleConfirm(): void {
    if (this.deleteSaleLoading()) return;
    this.deleteSaleConfirmVisible.set(false);
    this.deleteSaleError.set(null);
  }

  executeDeleteSale(): void {
    const id = this.editingSaleId();
    if (id == null || this.deleteSaleLoading() || !this.canDeleteSale()) return;

    this.deleteSaleError.set(null);
    this.deleteSaleLoading.set(true);
    this.saleService
      .deleteSale(id)
      .pipe(finalize(() => this.deleteSaleLoading.set(false)))
      .subscribe({
        next: (r) => {
          if (r.ok) {
            this.deleteSaleConfirmVisible.set(false);
            this.deleteSaleError.set(null);
            this.toastr.success(r.message ?? 'Venta eliminada correctamente');
            this.router.navigate(['/sales']);
          } else {
            this.deleteSaleError.set(apiFailureMessage(r, 'No se pudo eliminar la venta'));
          }
        },
        error: (err) => {
          this.deleteSaleError.set(httpErrorApiMessage(err, 'Error al eliminar la venta'));
        }
      });
  }

  closeLineModal(): void {
    this.lineModalShown.set(false);
    setTimeout(() => {
      this.lineModalOpen.set(false);
      this.lineTaxModalOpen.set(false);
    }, 200);
  }

  saveLine(): void {
    if (!this.canEditLines()) {
      this.toastr.warning(saleEditLevelTooltip(this.editLevel()));
      return;
    }
    if (this.lineForm.invalid) {
      this.lineForm.markAllAsTouched();
      return;
    }

    const v = this.lineForm.getRawValue();
    const qty = Number(v.quantity) ?? 1;
    const unitPrice = Number(v.unitPrice) ?? 0;
    const subtotalInput = qty * unitPrice;
    const taxesInput = this.lineFormTaxes();

    const invoiceId = this.editingSaleId();
    const idx = this.editingLineIndex();
    const isLineEdit = this.lineModalMode() === 'edit' && idx !== null;

    // Editar línea: solo estado local; se persiste con el PUT de toda la venta.
    // Agregar línea en venta existente: POST inmediato.
    if (invoiceId != null && !isLineEdit) {
      const taxesRequest: CreateSaleLineTaxRequest[] = taxesInput.map((t) => ({
        taxCode: t.taxCode,
        taxRate: t.taxRate,
        taxAmount: t.taxAmount,
        taxBase: t.taxBase,
        applicationType: t.applicationType,
        collectionType: t.collectionType
      }));

      const taxAmount = taxesRequest.reduce((acc, t) => acc + (Number(t.taxAmount) || 0), 0);
      const totalAmount = subtotalInput + taxAmount;

      const request: CreateSaleLineRequest = {
        productId: v.productId,
        description: v.description,
        measureUnitId: v.measureUnitId,
        taxProductCodeId: v.taxProductCodeId,
        satObjetoImpId: v.satObjetoImpId,
        quantity: qty,
        unitPrice: unitPrice,
        subtotal: subtotalInput,
        taxAmount: Math.max(0, taxAmount),
        totalAmount: Math.max(0, totalAmount),
        taxExempt: v.taxExempt ?? false,
        taxInclusive: v.taxInclusive ?? false,
        taxes: taxesRequest,
        metadata: undefined
      };

      this.lineSaving.set(true);
      this.saleService
        .addSaleLine(invoiceId, request)
        .pipe(finalize(() => this.lineSaving.set(false)))
        .subscribe({
          next: (res) => {
            if (res.ok && res.body) {
              this.toastr.success('Línea guardada correctamente');
              this.currentSale.set(res.body);
              this.patchSale(res.body);
              this.lines.set((res.body.lines ?? []).map((l) => this.lineToVm(l)));
              this.draftButtonRefresh.update((n) => n + 1);
              this.closeLineModal();
              this.cdr.detectChanges();
            } else {
              this.setError(res.message || 'Error al guardar la línea.');
            }
          },
          error: (err) => {
            this.setError(err.error?.message || 'Error al guardar la línea.');
          }
        });
      return;
    }

    const previewRequest: PreviewSaleLineRequest = {
      subtotal: subtotalInput,
      taxInclusive: v.taxInclusive ?? false,
      taxes: (taxesInput ?? []).map((t) => ({
        taxCode: t.taxCode,
        taxRate: t.taxRate,
        taxAmount: t.taxAmount,
        taxBase: t.taxBase,
        applicationType: t.applicationType,
        collectionType: t.collectionType
      }))
    };

    this.lineSaving.set(true);
    this.saleService
      .previewSaleLine(previewRequest)
      .pipe(finalize(() => this.lineSaving.set(false)))
      .subscribe({
        next: (res) => {
          if (!res.ok || !res.body) {
            this.setError(res.message || 'No se pudo calcular el preview de la línea.');
            return;
          }
          const lineDto = res.body;
          const taxesFromPreview: CreateSaleLineTaxRequest[] = (lineDto.taxes ?? []).map((t, i) => {
            const inputTax = taxesInput[i];
            return {
              taxCode: t.tax?.taxKey,
              taxRate: t.taxRate,
              taxAmount: t.taxAmount,
              taxBase: t.taxBase,
              applicationType: toLineTaxApplicationTypeForCreate(t.applicationType as TaxApplicationType),
              collectionType: (inputTax?.collectionType ?? t.collectionType ?? 'SELLER_COLLECTS') as 'SELLER_COLLECTS' | 'SELLER_WITHHOLDS'
            };
          });

          const satObjetoImp = (v.satObjetoImpSelection as SATObjetoImpDTO) ?? (v.satObjetoImpId ? this.satObjetoImpOptions().find((s) => s.id === v.satObjetoImpId) ?? { id: v.satObjetoImpId, name: '' } : undefined);
          const vm: SaleLineVm = {
            tempId: `line-${Date.now()}`,
            productId: v.productId,
            product: v.productSelection,
            description: v.description,
            quantity: qty,
            unitPrice,
            subtotal: Number(lineDto.subtotal ?? subtotalInput),
            taxAmount: Number(lineDto.taxAmount ?? 0),
            totalAmount: Number(lineDto.totalAmount ?? subtotalInput),
            measureUnitId: v.measureUnitId,
            measureUnit: v.measureUnitSelection,
            taxProductCodeId: v.taxProductCodeId,
            taxProductCode: v.taxProductCodeSelection,
            satObjetoImpId: v.satObjetoImpId ?? undefined,
            satObjetoImp: satObjetoImp,
            taxExempt: v.taxExempt,
            taxInclusive: v.taxInclusive,
            taxes: taxesFromPreview
          };

          if (isLineEdit) {
            vm.id = this.lines()[idx].id;
            vm.tempId = this.lines()[idx].tempId;
            this.lines.update((arr) => {
              const next = [...arr];
              next[idx] = vm;
              return next;
            });
          } else {
            this.lines.update((arr) => [...arr, vm]);
          }

          // Patch totales de inmediato para que el botón Guardar borrador no quede deshabilitado por desfase del effect
          const t = this.totals();
          this.generalForm.patchValue(
            { subtotal: t.subtotal, tax: t.tax, traslados: t.traslados, retenciones: t.retenciones, discount: 0, total: t.total },
            { emitEvent: false }
          );
          this.generalForm.updateValueAndValidity({ emitEvent: false });
          this.draftButtonRefresh.update((n) => n + 1);
          this.closeLineModal();
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.setError(err.error?.message || 'Error al calcular impuestos de la línea.');
        }
      });
  }

  removeLine(index: number): void {
    if (!this.canEditLines()) {
      this.toastr.warning(saleEditLevelTooltip(this.editLevel()));
      return;
    }
    const invoiceId = this.editingSaleId();
    const line = this.lines()[index];
    const lineId = line?.id;

    // Si la factura existe en backend y la línea tiene id, llamar al API
    if (invoiceId != null && typeof lineId === 'number') {
      this.lineRemoving.set(true);
      this.saleService
        .removeSaleLine(invoiceId, lineId)
        .pipe(finalize(() => this.lineRemoving.set(false)))
        .subscribe({
          next: (r) => {
            if (r.ok && r.body) {
              this.currentSale.set(r.body);
              this.patchSale(r.body);
              const lineVms: SaleLineVm[] = (r.body.lines ?? []).map((l) => this.lineToVm(l));
              this.lines.set(lineVms);
              this.expandedLineIndices.set(new Set());
              if (this.lineModalOpen()) this.closeLineModal();
              this.toastr.success('Línea eliminada correctamente.');
            } else {
              this.setError(r.message || 'Error al eliminar la línea.');
            }
          },
          error: (err) => {
            this.setError(err.error?.message || 'Error al eliminar la línea.');
          }
        });
      return;
    }

    // Línea local (sin id): eliminar solo en memoria
    this.lines.update((arr) => arr.filter((_, i) => i !== index));
    this.expandedLineIndices.update((set) => {
      return new Set(
        [...set].filter((i) => i !== index).map((i) => (i > index ? i - 1 : i))
      );
    });
    this.draftButtonRefresh.update((v) => v + 1);
    this.cdr.detectChanges();
    if (this.lineModalOpen()) this.closeLineModal();
  }

  toggleLineExpanded(index: number): void {
    this.expandedLineIndices.update((set) => {
      const next = new Set(set);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }

  isLineExpanded(index: number): boolean {
    return this.expandedLineIndices().has(index);
  }

  editLineTaxInModal(taxIndex: number | null): void {
    this.editingLineTaxIndex.set(taxIndex);
    const taxes = this.lineFormTaxes();
    if (taxIndex !== null && taxes[taxIndex]) {
      const t = taxes[taxIndex];
      const taxOpt = this.taxOptions().find((x) => x.taxKey === t.taxCode) ?? null;
      this.lineTaxForm.patchValue({
        taxSelection: taxOpt,
        taxCode: t.taxCode,
        taxRate: t.taxRate ?? null,
        taxAmount: t.taxAmount ?? null,
        taxBase: t.taxBase ?? '',
        applicationType: t.applicationType ?? 'TAXABLE',
        collectionType: t.collectionType ?? 'SELLER_COLLECTS'
      });
    } else {
      this.lineTaxForm.reset({
        taxSelection: null,
        taxCode: '',
        taxRate: null,
        taxAmount: null,
        taxBase: '',
        applicationType: 'TAXABLE',
        collectionType: 'SELLER_COLLECTS'
      });
    }
    this.lineTaxModalOpen.set(true);
    setTimeout(() => this.lineTaxModalShown.set(true), 10);
  }

  closeLineTaxModal(): void {
    this.lineTaxModalShown.set(false);
    setTimeout(() => {
      this.lineTaxModalOpen.set(false);
      this.editingLineTaxIndex.set(null);
    }, 200);
  }

  private setupLineTaxFormExclusiveFields(): void {
    const taxRateCtrl = this.lineTaxForm.get('taxRate');
    const taxAmountCtrl = this.lineTaxForm.get('taxAmount');
    if (!taxRateCtrl || !taxAmountCtrl) return;

    taxRateCtrl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (this.hasLineTaxValue(value)) {
          taxAmountCtrl.setValue(null, { emitEvent: false });
        }
      });

    taxAmountCtrl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (this.hasLineTaxValue(value)) {
          taxRateCtrl.setValue(null, { emitEvent: false });
        }
      });
  }

  private hasLineTaxValue(value: unknown): boolean {
    return value !== null && value !== undefined && value !== '';
  }

  saveLineTaxInModal(): void {
    if (this.lineTaxForm.invalid) return;
    const v = this.lineTaxForm.getRawValue();
    const tax = v.taxSelection as TaxDTO | null;
    if (!tax) return;

    const req: CreateSaleLineTaxRequest = {
      taxCode: tax.taxKey,
      taxRate: v.taxRate != null && v.taxRate !== '' ? Number(v.taxRate) : undefined,
      taxAmount: v.taxAmount != null && v.taxAmount !== '' ? Number(v.taxAmount) : undefined,
      taxBase: v.taxBase ? Number(v.taxBase) : undefined,
      applicationType: v.applicationType,
      collectionType: v.collectionType ?? 'SELLER_COLLECTS'
    };

    const ti = this.editingLineTaxIndex();
    this.lineFormTaxes.update((arr) => {
      const next = [...arr];
      if (ti !== null) {
        next[ti] = req;
      } else {
        next.push(req);
      }
      return next;
    });

    this.closeLineTaxModal();
  }

  removeLineTaxInModal(taxIndex: number): void {
    this.lineFormTaxes.update((arr) => arr.filter((_, i) => i !== taxIndex));
  }

  getTaxApplicationLabel(t: TaxApplicationType): string {
    return this.taxApplicationOptions.find((o) => o.value === t)?.label ?? t;
  }

  getTaxCollectionTypeLabel(type: string | undefined): string {
    return this.taxCollectionTypeOptions.find((o) => o.value === type)?.label ?? '-';
  }

  /** Devuelve "Nombre (código)" del impuesto, o solo el código si no se encuentra. */
  getTaxDisplayWithCode(taxCode: string | undefined): string {
    if (!taxCode) return '-';
    const tax = this.taxOptions().find((x) => x.taxKey === taxCode);
    if (tax?.displayName) return `${taxCode} - ${tax.displayName}`;
    return taxCode;
  }

  /** Formato de tasa para vista: si viene como decimal (0.16) muestra 16%, si viene como entero (16) muestra 16%. */
  formatTaxRate(rate: number | undefined | null): string {
    if (rate == null) return '-';
    if (rate > 0 && rate <= 1) return `${(rate * 100).toFixed(2)}%`;
    return `${Number(rate).toFixed(2)}%`;
  }

  private setError(msg: string): void {
    this.error.set(msg);
    this.toastr.error(msg);
  }

  loadAttachments(): void {
    const saleId = this.editingSaleId();
    if (!saleId) {
      this.attachments.set([]);
      return;
    }
    this.attachmentsLoading.set(true);
    this.saleService.listSaleAttachments(saleId).pipe(
      finalize(() => {
        this.attachmentsLoading.set(false);
        this.cdr.markForCheck();
      })
    ).subscribe({
      next: (response) => {
        if (response.ok) {
          this.attachments.set(response.body ?? []);
        } else {
          this.toastr.error(response.message ?? 'Error al cargar adjuntos');
        }
      },
      error: () => this.toastr.error('Error al cargar adjuntos')
    });
  }

  async onAttachmentSelect(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    const saleId = this.editingSaleId();
    if (!saleId || files.length === 0) {
      return;
    }

    const MAX_SIZE_MB = 10;
    const maxBytes = MAX_SIZE_MB * 1024 * 1024;
    this.uploadingAttachments.set(true);
    let uploaded = 0;

    try {
      for (const file of files) {
        if (file.size > maxBytes) {
          this.toastr.error(`${file.name} supera ${MAX_SIZE_MB} MB`);
          continue;
        }
        const contentBase64 = await this.readFileAsBase64(file);
        const response = await new Promise<{ ok?: boolean; message?: string; body?: ReadFileResponse }>((resolve, reject) => {
          this.saleService.attachSaleFile(saleId, {
            name: file.name,
            contentType: file.type || 'application/octet-stream',
            contentBase64
          }).subscribe({ next: resolve, error: reject });
        });
        if (response.ok && response.body) {
          uploaded += 1;
        } else {
          this.toastr.error(response.message ?? `Error al subir ${file.name}`);
        }
      }
      if (uploaded > 0) {
        this.toastr.success(uploaded === 1 ? 'Archivo adjuntado correctamente' : `${uploaded} archivos adjuntados`);
        this.loadAttachments();
      }
    } catch (err: unknown) {
      const message = (err as { error?: { message?: string } })?.error?.message ?? 'Error al subir el archivo';
      this.toastr.error(message);
    } finally {
      this.uploadingAttachments.set(false);
      this.cdr.markForCheck();
    }
  }

  openAttachment(file: ReadFileResponse): void {
    if (!file.content) {
      this.toastr.error('No hay URL de descarga para este archivo');
      return;
    }
    window.open(file.content, '_blank', 'noopener');
  }

  confirmDeleteAttachment(file: ReadFileResponse): void {
    if (file.id == null) {
      return;
    }
    const saleId = this.editingSaleId();
    if (!saleId) {
      return;
    }
    this.confirmationService.confirm({
      message: `¿Eliminar el archivo "${file.name ?? 'adjunto'}"?`,
      header: 'Confirmar eliminación',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, eliminar',
      rejectLabel: 'Cancelar',
      accept: () => {
        this.saleService.deleteSaleAttachment(saleId, file.id!).pipe(
          finalize(() => this.cdr.markForCheck())
        ).subscribe({
          next: (res) => {
            if (res?.ok !== false) {
              this.attachments.update((list) => list.filter((f) => f.id !== file.id));
              this.toastr.success('Archivo eliminado correctamente');
            } else {
              this.toastr.error(res?.message ?? 'Error al eliminar el archivo');
            }
          },
          error: (err) => {
            this.toastr.error(err.error?.message ?? 'Error al eliminar el archivo');
          }
        });
      }
    });
  }

  formatFileSize(size?: number): string {
    if (size == null || !Number.isFinite(size)) {
      return '-';
    }
    if (size < 1024) {
      return `${size} B`;
    }
    if (size < 1024 * 1024) {
      return `${(size / 1024).toFixed(1)} KB`;
    }
    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  }

  getFileIcon(contentType?: string): string {
    const type = (contentType ?? '').toLowerCase();
    if (type.startsWith('image/')) return 'pi-image';
    if (type === 'application/pdf') return 'pi-file-pdf';
    if (type.includes('spreadsheet') || type.includes('excel')) return 'pi-file-excel';
    if (type.includes('word') || type.includes('msword')) return 'pi-file-word';
    return 'pi-file';
  }

  private readFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.includes(',') ? result.split(',')[1] : result;
        resolve(base64 ?? '');
      };
      reader.onerror = () => reject(reader.error ?? new Error('No se pudo leer el archivo'));
      reader.readAsDataURL(file);
    });
  }
}
